import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { interviewAPI, settingsAPI, ttsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import ReportIssueButton from '../components/ReportIssueButton';
import Editor from '@monaco-editor/react';
import { Modal, Box } from '@mui/material';

// Add pulse animation for recording indicator
const pulseKeyframes = `
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

function TakeInterview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [interview, setInterview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [started, setStarted] = useState(false);
  const [mediaStream, setMediaStream] = useState(null);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const recordedChunksRef = useRef([]); // Use ref instead of state for better performance
  const [isRecording, setIsRecording] = useState(false);
  const [recognition, setRecognition] = useState(null);
  const recognitionRef = useRef(null);
  const [isListening, setIsListening] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [_transcripts, setTranscripts] = useState({});
  const [currentTranscript, setCurrentTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [questionStartTime, setQuestionStartTime] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null); // 'success', 'error', or null
  const [uploadError, setUploadError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [isRecognitionReady, setIsRecognitionReady] = useState(false);
  const videoRef = useRef(null);
  
  // Follow-up question states
  const [pendingFollowUps, setPendingFollowUps] = useState([]);
  const [currentFollowUpIndex, setCurrentFollowUpIndex] = useState(0);
  const [isAnsweringFollowUp, setIsAnsweringFollowUp] = useState(false);
  const [currentFollowUpParent, setCurrentFollowUpParent] = useState(null);
  const [currentFollowUpMetadata, setCurrentFollowUpMetadata] = useState(null); // Store metadata like isNested, parentFollowUpQuestion
  // eslint-disable-next-line no-unused-vars
  const [_followUpAnswers, setFollowUpAnswers] = useState({});
  const [isGeneratingFollowUp, setIsGeneratingFollowUp] = useState(false);
  
  // Coding question states
  const [codeAnswers, setCodeAnswers] = useState({}); // Stores code for main questions
  const [followUpCodeAnswers, setFollowUpCodeAnswers] = useState({}); // Stores code for follow-up questions
  const [selectedLanguage, setSelectedLanguage] = useState('javascript'); // Current language for coding questions
  const [editorTheme, setEditorTheme] = useState('vs-dark'); // Monaco editor theme
  
  // Ask interviewer feature (for Coding questions)
  const [isAsking, setIsAsking] = useState(false);
  const [askTranscript, setAskTranscript] = useState('');
  const [askInterimTranscript, setAskInterimTranscript] = useState('');
  const [aiInterviewerResponse, setAiInterviewerResponse] = useState(null);
  const [isWaitingForAIResponse, setIsWaitingForAIResponse] = useState(false);
  const askRecognitionRef = useRef(null);
  const [askConversationHistory, setAskConversationHistory] = useState([]); // Store conversation history
  
  // Video chapters tracking
  const videoStartTimeRef = useRef(null); // When video recording started
  const chaptersRef = useRef([]); // Array of chapter timestamps
  
  // Text-to-speech
  const speechSynthesisRef = useRef(null);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const currentAudioRef = useRef(null);
  const [voiceProvider, setVoiceProvider] = useState('webspeech'); // Default to browser speech to avoid API errors
  
  // View toggle for transcript and code editor
  const [activeView, setActiveView] = useState(null); // null, 'text', or 'code'
  
  // Auto-save timer for code
  const autoSaveTimerRef = useRef(null);

  // Load saved code from localStorage on component mount
  useEffect(() => {
    if (!interview || !id) return;
    
    try {
      const storageKey = `interview_${id}_code`;
      const savedData = localStorage.getItem(storageKey);
      
      if (savedData) {
        const parsed = JSON.parse(savedData);
        
        // Restore main question codes
        if (parsed.codeAnswers) {
          setCodeAnswers(parsed.codeAnswers);
          console.log('Restored code answers from localStorage');
        }
        
        // Restore follow-up codes
        if (parsed.followUpCodeAnswers) {
          setFollowUpCodeAnswers(parsed.followUpCodeAnswers);
          console.log('Restored follow-up code answers from localStorage');
        }
        
        // Restore selected language
        if (parsed.selectedLanguage) {
          setSelectedLanguage(parsed.selectedLanguage);
        }
      }
    } catch (error) {
      console.error('Error loading code from localStorage:', error);
    }
  }, [interview, id]);

  // Auto-save code to localStorage as user types (debounced)
  useEffect(() => {
    if (!started || !interview || !id) return;
    
    // Clear existing timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    
    // Set new timer to auto-save after 2 seconds of no typing
    autoSaveTimerRef.current = setTimeout(() => {
      try {
        const storageKey = `interview_${id}_code`;
        const dataToSave = {
          codeAnswers,
          followUpCodeAnswers,
          selectedLanguage,
          timestamp: Date.now()
        };
        
        localStorage.setItem(storageKey, JSON.stringify(dataToSave));
        console.log('Code auto-saved to localStorage');
      } catch (error) {
        console.error('Error auto-saving code to localStorage:', error);
        // Silently fail - don't interrupt user
      }
    }, 2000); // 2 second debounce
    
    // Cleanup timer on unmount
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [codeAnswers, followUpCodeAnswers, selectedLanguage, started, interview, id]);

  useEffect(() => {
    fetchInterview();
    fetchVoiceSettings();
    
    // Cleanup function
    return () => {
      if (mediaStream) {
        mediaStream.getTracks().forEach(track => track.stop());
      }
      if (recognition) {
        recognition.stop();
      }
      if (askRecognitionRef.current) {
        askRecognitionRef.current.stop();
      }
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (started && timeRemaining > 0) {
      const timer = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            handleAutoSubmit('timeout');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [started, timeRemaining]);

  // Handle page navigation/close - auto submit interview
  useEffect(() => {
    if (!started || isSubmitting) return;

    const handleBeforeUnload = async (e) => {
      e.preventDefault();
      e.returnValue = 'Your interview is in progress. If you leave, it will be automatically submitted.';
      
      // Try to submit the interview
      try {
        await stopRecording();
        
        // Save current answer
        const currentQuestionId = interview.questions[currentQuestionIndex].questionId._id;
        const timeSpent = saveQuestionTiming(currentQuestionId);
        
        let finalTranscript = currentTranscript.trim();
        if (interimTranscript.trim()) {
          finalTranscript += ' ' + interimTranscript.trim();
        }
        
        if (finalTranscript) {
          // Use sendBeacon for reliable submission during page unload
          const data = JSON.stringify({
            questionId: currentQuestionId,
            answer: finalTranscript,
            timeSpent
          });
          navigator.sendBeacon(`/api/interview/${id}/answer`, data);
        }
        
        // Submit interview using sendBeacon for reliability
        navigator.sendBeacon(`/api/interview/${id}/submit`);
      } catch (error) {
        console.error('Error during page unload:', error);
      }
      
      return e.returnValue;
    };

    const handleVisibilityChange = async () => {
      if (document.hidden && started && !isSubmitting) {
        // User switched tabs or minimized - auto submit
        console.log('Page hidden - auto submitting interview');
        try {
          await handleAutoSubmit('tab-switch');
        } catch (error) {
          console.error('Error auto-submitting on visibility change:', error);
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [started, isSubmitting, currentQuestionIndex, currentTranscript, interimTranscript]);

  const fetchVoiceSettings = async () => {
    try {
      const response = await settingsAPI.getSettings();
      const provider = response.data.settings.voiceProvider || 'webspeech';
      setVoiceProvider(provider);
      console.log('Voice provider loaded from settings:', provider);
    } catch (error) {
      console.error('Error fetching voice settings:', error);
      // Default to browser speech if fetch fails to avoid API errors
      setVoiceProvider('webspeech');
      console.log('Voice provider defaulting to: webspeech (browser)');
    }
  };

  const handleManualRestartRecognition = async () => {
    try {
      console.log('Manually restarting speech recognition...');
      
      // Stop if currently running
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          console.log('Recognition already stopped or not started');
        }
      }

      // Wait a bit for cleanup
      await new Promise(resolve => setTimeout(resolve, 500));

      // Reinitialize recognition
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SpeechRecognition) {
        alert('Speech recognition is not supported in your browser. Please use Chrome, Edge, or Safari.');
        return;
      }

      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      recognition.onstart = () => {
        console.log('Manual restart: Speech recognition started');
        setIsListening(true);
        setIsRecognitionReady(true);
      };

      recognition.onresult = (event) => {
        let interimText = '';
        let finalText = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalText += transcript + ' ';
          } else {
            interimText += transcript;
          }
        }
        
        if (finalText) {
          console.log('Manual restart: Final transcript captured:', finalText);
          setCurrentTranscript(prev => prev + finalText);
        }
        
        setInterimTranscript(interimText);
      };

      recognition.onerror = (event) => {
        console.error('Manual restart recognition error:', event.error);
        if (event.error !== 'no-speech') {
          setIsListening(false);
        }
      };

      recognition.onend = () => {
        console.log('Manual restart: Recognition ended, attempting auto-restart');
        // Auto-restart recognition unless we're processing or submitting
        if (started && !isProcessing && !isSubmitting) {
          console.log('Auto-restarting recognition after manual restart');
          setTimeout(() => {
            if (recognition && started && !isProcessing && !isSubmitting) {
              try {
                recognition.start();
              } catch (e) {
                console.error('Error auto-restarting after manual restart:', e);
              }
            }
          }, 100);
        }
      };

      recognitionRef.current = recognition;
      setRecognition(recognition); // Update the state so other parts of the code use the new instance
      
      // Start recognition
      await new Promise(resolve => setTimeout(resolve, 1000));
      recognition.start();
      console.log('Manual restart: Recognition start() called');
      
    } catch (error) {
      console.error('Error during manual restart:', error);
      alert('Failed to restart speech recognition. Please try again or refresh the page.');
    }
  };

  const fetchInterview = async () => {
    setLoading(true);
    try {
      const response = await interviewAPI.getInterview(id);
      setInterview(response.data.interview);
      
      // Check if can start - comparing local time with server UTC times
      const now = new Date();
      const startDate = new Date(response.data.interview.startDate);
      const endDate = new Date(response.data.interview.endDate);
      
      console.log('Time check:', {
        now: now.toLocaleString(),
        nowUTC: now.toISOString(),
        startDate: startDate.toLocaleString(),
        startDateUTC: startDate.toISOString(),
        endDate: endDate.toLocaleString(),
        endDateUTC: endDate.toISOString(),
        canStart: now >= startDate && now <= endDate
      });
      
      if (now < startDate) {
        const timeUntilStart = Math.ceil((startDate - now) / (1000 * 60)); // minutes
        alert(`This interview has not started yet. It will start in ${timeUntilStart} minute(s) at ${startDate.toLocaleString()}`);
        navigate('/my-interviews');
        return;
      }
      
      if (now > endDate) {
        alert(`This interview has ended at ${endDate.toLocaleString()}.`);
        navigate('/my-interviews');
        return;
      }
    } catch (error) {
      console.error('Error fetching interview:', error);
      alert('Failed to load interview');
      navigate('/my-interviews');
    }
    setLoading(false);
  };

  const handleStart = async () => {
    // Prevent multiple clicks
    if (isStarting) {
      console.log('Interview is already starting, ignoring duplicate click');
      return;
    }
    
    setIsStarting(true);
    try {
      // Determine time of day for greeting
      const hour = new Date().getHours();
      let greeting;
      if (hour < 12) {
        greeting = 'Morning';
      } else if (hour < 17) {
        greeting = 'Afternoon';
      } else {
        greeting = 'Evening';
      }
      
      // Get student's first name
      const studentName = user?.name?.split(' ')[0] || 'there';
      
      // Create greeting message
      const greetingMessage = `${greeting} ${studentName}! Welcome to your interview. Take your time and do your best. Let's begin.`;
      
      // Speak the greeting using configured voice provider
      setIsSpeaking(true);
      
      console.log('Starting interview with voice provider:', voiceProvider);
      
      try {
        if (voiceProvider === 'elevenlabs') {
          try {
            // Use ElevenLabs
            const audioBlob = await textToSpeech(greetingMessage);
            const audioUrl = URL.createObjectURL(audioBlob);
            const audio = new Audio(audioUrl);
            currentAudioRef.current = audio;
            
            audio.onended = async () => {
              setIsSpeaking(false);
              URL.revokeObjectURL(audioUrl);
              currentAudioRef.current = null;
              await startActualInterview();
            };
            
            audio.onerror = async (error) => {
              console.error('Audio playback error:', error);
              setIsSpeaking(false);
              currentAudioRef.current = null;
              await startActualInterview();
            };
            
            await audio.play();
          } catch (error) {
            console.error('ElevenLabs TTS failed for greeting, falling back to Web Speech API:', error);
            // Fallback to Web Speech API
            fallbackGreeting(greetingMessage, startActualInterview);
          }
        } else {
          // Use Web Speech API
          fallbackGreeting(greetingMessage, startActualInterview);
        }
      } catch (speechError) {
        console.error('Text-to-speech error:', speechError);
        setIsSpeaking(false);
        // Continue with interview even if TTS fails
        await startActualInterview();
      }
      
    } catch (error) {
      console.error('Error starting interview:', error);
      console.error('Error details:', error.response?.data);
      setIsStarting(false);
      if (error.name === 'NotAllowedError') {
        alert('Please allow camera and microphone access to start the interview.');
      } else {
        const errorMessage = error.response?.data?.message || error.message || 'Failed to start interview';
        alert(errorMessage);
        console.error('Full error:', JSON.stringify(error.response?.data || error.message));
      }
    }
  };

  const startActualInterview = async () => {
    try {
      // Check if video recording is required for this interview
      const requireVideo = interview.requireVideoRecording !== false; // Default to true if not set
      
      let recorder = null; // Declare in outer scope
      
      if (requireVideo) {
        // Check if mediaDevices is supported
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          alert('Your browser does not support camera/microphone access. Please use a modern browser like Chrome, Firefox, or Edge, and ensure you are accessing the site via HTTPS.');
          return;
        }

        // Request camera and microphone permissions with optimized constraints
        const stream = await navigator.mediaDevices.getUserMedia({ 
          video: {
            width: { ideal: 1280 },
            height: { ideal: 720 },
            frameRate: { ideal: 30, max: 30 }
          }, 
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            sampleRate: 48000
          }
        });
        
        setMediaStream(stream);
        
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.muted = true; // Mute to avoid feedback
        }

        // Initialize MediaRecorder with optimized settings for perfect audio-video sync
        let mimeType = 'video/webm;codecs=vp8,opus'; // VP8 has better sync than VP9
        
        // Check support and fallback
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = 'video/webm;codecs=h264,opus';
        }
        
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = 'video/webm';
        }
        
        console.log('Using MIME type:', mimeType);
        
        recorder = new MediaRecorder(stream, {
          mimeType: mimeType,
          videoBitsPerSecond: 1000000, // 1 Mbps - lower bitrate for faster encoding
          audioBitsPerSecond: 128000   // 128 kbps for clear audio
        });
        
        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) {
            recordedChunksRef.current.push(e.data);
          }
        };
        
        recorder.onerror = (event) => {
          console.error('MediaRecorder error:', event.error);
        };
        
        setMediaRecorder(recorder);
      } else {
        console.log('Video recording not required for this interview');
      }
      
      // Initialize Speech Recognition
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      let recognitionInstance = null;
      
      if (SpeechRecognition) {
        try {
          recognitionInstance = new SpeechRecognition();
          recognitionInstance.continuous = true;
          recognitionInstance.interimResults = true;
          recognitionInstance.lang = 'en-US';
          
          recognitionInstance.onstart = () => {
            console.log('Speech recognition started');
            setIsRecognitionReady(true);
            setIsListening(true);
          };
          
          recognitionInstance.onresult = (event) => {
            let interimText = '';
            let finalText = '';
            
            for (let i = event.resultIndex; i < event.results.length; i++) {
              const transcriptPart = event.results[i][0].transcript;
              if (event.results[i].isFinal) {
                finalText += transcriptPart + ' ';
              } else {
                interimText += transcriptPart;
              }
            }
            
            if (finalText) {
              console.log('Final transcript captured:', finalText);
              setCurrentTranscript(prev => prev + finalText);
            }
            
            setInterimTranscript(interimText);
          };
          
          recognitionInstance.onend = () => {
            console.log('Speech recognition ended, attempting auto-restart');
            setInterimTranscript('');
            // Auto-restart recognition unless we're processing or submitting
            if (started && !isProcessing && !isSubmitting) {
              console.log('Auto-restarting recognition to keep it active');
              setTimeout(() => {
                if (recognitionInstance && started && !isProcessing && !isSubmitting) {
                  try {
                    recognitionInstance.start();
                  } catch (e) {
                    console.error('Could not auto-restart recognition:', e);
                    setIsRecognitionReady(false);
                    setIsListening(false);
                  }
                }
              }, 100);
            } else {
              console.log('Not restarting - started:', started, 'isProcessing:', isProcessing, 'isSubmitting:', isSubmitting);
              setIsRecognitionReady(false);
              setIsListening(false);
            }
          };
          
          recognitionInstance.onerror = (event) => {
            console.error('Speech recognition error:', event.error);
            setIsRecognitionReady(false);
            setIsListening(false);
            // Only auto-restart for certain errors and not during processing
            if (event.error !== 'aborted' && event.error !== 'no-speech' && !isProcessing && started) {
              console.log('Attempting to restart recognition after error');
              setTimeout(() => {
                if (recognitionInstance && started && !isProcessing) {
                  try {
                    recognitionInstance.start();
                  } catch (e) {
                    console.error('Could not restart recognition:', e);
                  }
                }
              }, 1500);
            }
          };
          
          setRecognition(recognitionInstance);
        } catch (error) {
          console.warn('Speech recognition initialization failed:', error);
          console.log('Continuing without speech recognition');
        }
      } else {
        console.warn('Speech recognition is not supported in this browser.');
        console.log('Interview will continue without speech-to-text feature.');
      }

      // Request fullscreen
      try {
        const elem = document.documentElement;
        if (elem.requestFullscreen) {
          await elem.requestFullscreen();
        } else if (elem.webkitRequestFullscreen) { /* Safari */
          await elem.webkitRequestFullscreen();
        } else if (elem.msRequestFullscreen) { /* IE11 */
          await elem.msRequestFullscreen();
        }
      } catch (error) {
        console.log('Fullscreen request failed:', error);
      }

      // Start the interview
      await interviewAPI.startInterview(id);
      setStarted(true);
      setTimeRemaining(interview.duration * 60);
      
      // Start recording if video is required
      if (interview.requireVideoRecording !== false && recorder) {
        recorder.start(1000);
        setIsRecording(true);
        
        // Record video start time for chapter tracking
        videoStartTimeRef.current = Date.now();
        
        // Add first chapter for first question
        chaptersRef.current = [{
          questionText: interview.questions[0]?.questionId?.question || 'Question 1',
          questionNumber: 1,
          isFollowUp: false,
          timestamp: 0 // First question starts at beginning
        }];
      }
      
      // Don't start speech recognition immediately - wait until question is spoken
      // This prevents the computer's voice from being captured in the transcript
      
      // Start timing for first question
      setQuestionStartTime(Date.now());
      
      // Read the first question after 1.5 seconds (giving time after greeting)
      setTimeout(() => {
        const firstQuestion = interview.questions[0]?.questionId;
        if (firstQuestion?.question) {
          // Skip reading if it's a Coding question, but still start recognition
          if (firstQuestion.questionType === 'Coding') {
            console.log('Skipping TTS for Coding question');
            // Start speech recognition immediately for coding questions
            if (recognitionInstance) {
              console.log('Starting speech recognition without TTS for Coding question');
              recognitionInstance.start();
            }
          } else {
            // Read the question normally for non-Coding questions
            speakQuestion(firstQuestion.question, false, () => {
              // Start speech recognition after question finishes speaking
              if (recognitionInstance) {
                console.log('Starting speech recognition after question was spoken');
                recognitionInstance.start();
              }
            });
          }
        }
      }, 1500);
      
    } catch (error) {
      console.error('Error starting interview:', error);
      console.error('Error details:', error.response?.data);
      setIsStarting(false);
      if (error.name === 'NotAllowedError') {
        alert('Please allow camera and microphone access to start the interview.');
      } else {
        const errorMessage = error.response?.data?.message || error.message || 'Failed to start interview';
        alert(errorMessage);
        console.error('Full error:', JSON.stringify(error.response?.data || error.message));
      }
    }
  };

  const handleAnswerChange = async (questionId, answer, timeSpent = null, code = null, language = null) => {
    setAnswers(prev => ({ ...prev, [questionId]: answer }));
    
    // Auto-save answer and get follow-up questions if enabled
    if (started) {
      try {
        setIsGeneratingFollowUp(true);
        
        // Prepare payload
        const payload = {
          questionId,
          answer,
          timeSpent: timeSpent !== null ? timeSpent : ((interview.duration * 60) - timeRemaining) * 1000
        };
        
        // Add code fields if provided
        if (code !== null) {
          payload.code = code;
          payload.selectedLanguage = language || selectedLanguage;
        }
        
        const response = await interviewAPI.submitAnswer(id, payload);
        
        // Check if there are pending follow-up questions for this answer
        if (response.data.pendingFollowUps && response.data.pendingFollowUps.length > 0) {
          const followUpsForQuestion = response.data.pendingFollowUps.find(
            f => f.parentQuestionId === questionId || f.parentQuestionId?._id === questionId
          );
          if (followUpsForQuestion && followUpsForQuestion.questions.length > 0) {
            console.log('Follow-up questions received:', followUpsForQuestion.questions);
            console.log('Is nested follow-up:', followUpsForQuestion.isNested);
            console.log('Parent follow-up question:', followUpsForQuestion.parentFollowUpQuestion);
            
            // Add chapter for first follow-up question
            if (videoStartTimeRef.current) {
              const currentTimestamp = (Date.now() - videoStartTimeRef.current) / 1000; // Convert to seconds
              const newChapter = {
                questionText: followUpsForQuestion.questions[0].question,
                questionNumber: currentQuestionIndex + 1,
                isFollowUp: true,
                parentQuestionNumber: currentQuestionIndex + 1,
                timestamp: Math.round(currentTimestamp)
              };
              
              // Check if this chapter already exists (avoid duplicates)
              const isDuplicate = chaptersRef.current.some(ch => 
                ch.isFollowUp === true && 
                ch.parentQuestionNumber === newChapter.parentQuestionNumber &&
                ch.questionText === newChapter.questionText
              );
              
              if (!isDuplicate) {
                chaptersRef.current.push(newChapter);
              }
            }
            
            setPendingFollowUps(followUpsForQuestion.questions);
            setCurrentFollowUpParent(questionId);
            setCurrentFollowUpIndex(0);
            setIsAnsweringFollowUp(true);
            setCurrentFollowUpMetadata({
              isNested: followUpsForQuestion.isNested || false,
              parentFollowUpQuestion: followUpsForQuestion.parentFollowUpQuestion || null
            });
            setQuestionStartTime(Date.now());
            
            // Don't start speech recognition immediately - wait until question finishes speaking
            setIsGeneratingFollowUp(false);
            
            // Speak first follow-up question after longer delay to allow modal to close
            setTimeout(() => {
              if (followUpsForQuestion.questions[0]?.question) {
                speakQuestion(followUpsForQuestion.questions[0].question, true, () => {
                  // Start speech recognition after follow-up question finishes speaking
                  if (recognition && !isSubmitting) {
                    console.log('Starting speech recognition after follow-up question was spoken');
                    try {
                      recognition.start();
                    } catch (e) {
                      console.error('Error starting recognition for follow-up:', e);
                    }
                  }
                });
              }
            }, 1500);
            
            return true; // Indicate follow-ups were generated
          }
        }
        setIsGeneratingFollowUp(false);
        return false; // No follow-ups generated
      } catch (error) {
        console.error('Error saving answer:', error);
        setIsGeneratingFollowUp(false);
        return false;
      }
    }
    return false;
  };

  // Handle follow-up question answer
  const handleFollowUpAnswerChange = async (followUpQuestion, answer, timeSpent = null, skipNestedCheck = false, code = null, language = null) => {
    const key = `followup_${currentFollowUpParent}_${currentFollowUpIndex}`;
    setFollowUpAnswers(prev => ({ ...prev, [key]: answer }));
    
    if (started && !skipNestedCheck) {
      try {
        setIsGeneratingFollowUp(true);
        
        if (!interviewAPI || !interviewAPI.submitAnswer) {
          console.error('interviewAPI.submitAnswer is not available');
          setIsGeneratingFollowUp(false);
          return { hasNested: false };
        }
        
        // Prepare payload
        const payload = {
          isFollowUp: true,
          parentQuestionId: currentFollowUpParent,
          followUpQuestion: followUpQuestion,
          answer,
          timeSpent: timeSpent !== null ? timeSpent : ((interview.duration * 60) - timeRemaining) * 1000
        };
        
        // Add code fields if provided
        if (code !== null) {
          payload.code = code;
          payload.selectedLanguage = language || selectedLanguage;
        }
        
        const response = await interviewAPI.submitAnswer(id, payload);
        
        if (!response || !response.data) {
          console.error('Invalid response from submitAnswer:', response);
          setIsGeneratingFollowUp(false);
          return { hasNested: false };
        }
        
        console.log('Follow-up answer submitted. Response:', response.data);
        console.log('Pending follow-ups in response:', response.data.pendingFollowUps);
        
        // Check if there are nested follow-up questions (follow-ups for this follow-up)
        if (response.data.pendingFollowUps && response.data.pendingFollowUps.length > 0) {
          console.log('Found pending follow-ups, checking for nested ones...');
          
          const nestedFollowUps = response.data.pendingFollowUps.find(
            f => {
              console.log('Checking follow-up:', {
                isNested: f.isNested,
                parentQuestionId: f.parentQuestionId,
                currentFollowUpParent: currentFollowUpParent,
                match: f.isNested === true && (f.parentQuestionId === currentFollowUpParent || f.parentQuestionId?._id === currentFollowUpParent)
              });
              return f.isNested === true && (f.parentQuestionId === currentFollowUpParent || f.parentQuestionId?._id === currentFollowUpParent);
            }
          );
          
          console.log('Nested follow-ups found:', nestedFollowUps);
          
          if (nestedFollowUps && nestedFollowUps.questions.length > 0) {
            console.log('Nested follow-up questions received:', nestedFollowUps.questions);
            console.log('These are follow-ups for the follow-up question');
            
            // Replace remaining follow-ups with nested ones
            // Keep only up to current index, then add nested follow-ups
            const newPendingList = [
              ...pendingFollowUps.slice(0, currentFollowUpIndex + 1),
              ...nestedFollowUps.questions
            ];
            
            setPendingFollowUps(newPendingList);
            
            console.log(`Replaced remaining follow-ups with ${nestedFollowUps.questions.length} nested follow-up questions`);
            console.log('New pending list length:', newPendingList.length);
            
            // Return the nested metadata for the NEXT question
            setIsGeneratingFollowUp(false);
            return {
              hasNested: true,
              nestedMetadata: {
                isNested: true,
                parentFollowUpQuestion: followUpQuestion
              },
              newPendingListLength: newPendingList.length, // Return the new length
              newPendingList: newPendingList // Return the actual list for speaking the question
            };
          }
        }
        
        setIsGeneratingFollowUp(false);
        return { hasNested: false };
      } catch (error) {
        console.error('Error saving follow-up answer:', error);
        setIsGeneratingFollowUp(false);
        return { hasNested: false };
      }
    } else if (started && skipNestedCheck) {
      // Just submit the answer without checking for nested follow-ups
      try {
        if (interviewAPI && interviewAPI.submitAnswer) {
          // Prepare payload
          const payload = {
            isFollowUp: true,
            parentQuestionId: currentFollowUpParent,
            followUpQuestion: followUpQuestion,
            answer,
            timeSpent: timeSpent !== null ? timeSpent : ((interview.duration * 60) - timeRemaining) * 1000
          };
          
          // Add code fields if provided
          if (code !== null) {
            payload.code = code;
            payload.selectedLanguage = language || selectedLanguage;
          }
          
          await interviewAPI.submitAnswer(id, payload);
        } else {
          console.error('interviewAPI.submitAnswer is not available');
        }
      } catch (error) {
        console.error('Error saving follow-up answer:', error);
      }
    }
    return { hasNested: false };
  };

  // Move to next follow-up or continue to next main question
  const handleNextFollowUp = async () => {
    console.log('=== handleNextFollowUp START ===');
    console.log('Current follow-up index:', currentFollowUpIndex);
    console.log('Total follow-ups:', pendingFollowUps.length);
    console.log('Is answering follow-up:', isAnsweringFollowUp);
    
    // Close any open transcript or code editor views
    setActiveView(null);
    
    setIsProcessing(true);
    
    try {
      // Stop speech recognition
      if (recognition) {
        try {
          recognition.stop();
        } catch (e) {
          console.error('Error stopping recognition:', e);
        }
      }
      
      setIsListening(false);
      setIsRecognitionReady(false);
      
      // Wait for speech processing
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Capture final transcript for follow-up
      let finalTranscript = currentTranscript.trim();
      if (interimTranscript.trim()) {
        finalTranscript += ' ' + interimTranscript.trim();
      }
      
      // Check if this follow-up requires code
      const currentFollowUp = pendingFollowUps[currentFollowUpIndex];
      const requiresCode = currentFollowUp?.requiresCode || false;
      const followUpCodeKey = `${currentFollowUpParent}_${currentFollowUpIndex}`;
      // Get code if it exists, regardless of requiresCode flag (user may have used Code Editor button)
      const currentCode = followUpCodeAnswers[followUpCodeKey] || null;
      
      console.log('=== SAVING FOLLOW-UP ANSWER ===');
      console.log('Follow-up Question:', currentFollowUp?.question);
      console.log('Has Code:', !!currentCode);
      console.log('Code Length:', currentCode?.length || 0);
      console.log('Selected Language:', selectedLanguage);
      console.log('===============================');
      
      // Use the current pending list length by default
      let effectivePendingLength = pendingFollowUps.length;
      let effectivePendingList = pendingFollowUps; // Track the actual list to use
      
      // Save follow-up answer if there's content or code
      if ((finalTranscript || currentCode) && pendingFollowUps[currentFollowUpIndex]) {
        try {
          const timeSpent = questionStartTime ? Date.now() - questionStartTime : 0;
          const result = await handleFollowUpAnswerChange(
            pendingFollowUps[currentFollowUpIndex].question,
            finalTranscript || 'Code submission',
            timeSpent,
            false, // Don't skip nested check
            currentCode,
            currentCode ? selectedLanguage : null // Send language if code exists
          );
          
          // If nested follow-ups were added, update metadata for the next question
          if (result?.hasNested && result?.nestedMetadata) {
            setCurrentFollowUpMetadata(result.nestedMetadata);
          }
          
          // Use the new pending list length if nested follow-ups were added
          if (result?.newPendingListLength) {
            effectivePendingLength = result.newPendingListLength;
          }
          
          // Use the new pending list if it was returned (for speaking the correct question)
          if (result?.newPendingList) {
            effectivePendingList = result.newPendingList;
          }
          
          console.log('After saving answer - currentFollowUpIndex:', currentFollowUpIndex);
          console.log('Effective pending list length:', effectivePendingLength);
          console.log('Has more follow-ups?', currentFollowUpIndex < effectivePendingLength - 1);
        } catch (error) {
          console.error('Error saving follow-up answer in handleNextFollowUp:', error);
          // Continue with default values if error occurs
        }
      }
      
      // Clear transcripts
      setCurrentTranscript('');
      setInterimTranscript('');
      
      if (currentFollowUpIndex < effectivePendingLength - 1) {
        // More follow-ups to answer
        const nextFollowUpIndex = currentFollowUpIndex + 1;
        console.log('Moving to next follow-up:', nextFollowUpIndex);
        
        // Check if the NEXT follow-up question is the start of nested ones
        // If we just got nested follow-ups, the next question will be at currentFollowUpIndex + 1
        // and it should already have the metadata set above
        // If it's a regular follow-up (not nested), reset metadata
        const isNextQuestionNested = currentFollowUpMetadata?.isNested && nextFollowUpIndex === currentFollowUpIndex + 1;
        if (!isNextQuestionNested) {
          // Reset metadata for non-nested follow-up
          setCurrentFollowUpMetadata({
            isNested: false,
            parentFollowUpQuestion: null
          });
        }
        
        // Add chapter for next follow-up question
        if (videoStartTimeRef.current) {
          const currentTimestamp = (Date.now() - videoStartTimeRef.current) / 1000;
          const newChapter = {
            questionText: pendingFollowUps[nextFollowUpIndex].question,
            questionNumber: currentQuestionIndex + 1,
            isFollowUp: true,
            parentQuestionNumber: currentQuestionIndex + 1,
            timestamp: Math.round(currentTimestamp)
          };
          
          // Check if this chapter already exists (avoid duplicates)
          const isDuplicate = chaptersRef.current.some(ch => 
            ch.isFollowUp === true && 
            ch.parentQuestionNumber === newChapter.parentQuestionNumber &&
            ch.questionText === newChapter.questionText
          );
          
          if (!isDuplicate) {
            chaptersRef.current.push(newChapter);
          }
        }
        
        setCurrentFollowUpIndex(nextFollowUpIndex);
        setQuestionStartTime(Date.now());
        
        console.log('Updated state - currentFollowUpIndex:', nextFollowUpIndex);
        console.log('isAnsweringFollowUp:', isAnsweringFollowUp);
        console.log('pendingFollowUps.length:', pendingFollowUps.length);
        
        // Close processing modal first, then speak question
        setIsProcessing(false);
        
        // Wait briefly for modal to close, then speak the next follow-up question
        setTimeout(() => {
          // Speak the next follow-up question and start recognition after it finishes
          // Use the effective pending list to get the correct question (important for nested follow-ups)
          if (effectivePendingList[nextFollowUpIndex]?.question) {
            speakQuestion(effectivePendingList[nextFollowUpIndex].question, true, () => {
            // Start speech recognition after question finishes speaking
            if (recognition && !isSubmitting && started) {
              console.log('Starting speech recognition after follow-up question was spoken');
              try {
                recognition.start();
                console.log('Speech recognition started successfully');
                setIsRecognitionReady(true);
                setIsListening(true);
              } catch (e) {
                console.error('Error starting recognition:', e);
                setIsRecognitionReady(true); // Enable button even if start fails
              }
            } else {
              setIsRecognitionReady(true); // Enable button if no recognition
            }
          });
          }
        }, 500); // Small delay after modal closes
        
        console.log('=== handleNextFollowUp END (next follow-up) - isProcessing set to false ===');
      } else {
        // All follow-ups done, clear and continue to next main question or submit
        console.log('All follow-ups completed');
        console.log('Current follow-up index:', currentFollowUpIndex);
        console.log('Effective pending length:', effectivePendingLength);
        
        // Move to next main question if not the last one
        if (currentQuestionIndex < interview.questions.length - 1) {
          console.log('Moving to next main question');
          
          // Clear follow-up states
          setIsAnsweringFollowUp(false);
          setPendingFollowUps([]);
          setCurrentFollowUpIndex(0);
          setCurrentFollowUpParent(null);
          
          // Proceed to next question
          await proceedToNextQuestion();
        } else {
          // Last question - all done, ready to submit
          console.log('Last question completed, ready to submit');
          
          // Clear follow-up states
          setIsAnsweringFollowUp(false);
          setPendingFollowUps([]);
          setCurrentFollowUpIndex(0);
          setCurrentFollowUpParent(null);
          setIsProcessing(false);
          setIsRecognitionReady(true); // Enable submit button
          
          // Show a brief success message
          setTimeout(() => {
            if (window.confirm('All questions completed! Click OK to submit your interview.')) {
              handleSubmit();
            } else {
              // User cancelled, keep them ready to submit
              setIsRecognitionReady(true);
            }
          }, 500);
        }
      }
    } catch (error) {
      console.error('Error in handleNextFollowUp:', error);
      setIsProcessing(false);
      setIsRecognitionReady(true); // Enable button even on error
    }
  };

  const saveQuestionTiming = (questionId) => {
    if (questionStartTime) {
      const timeSpent = Date.now() - questionStartTime;
      // Timing will be sent to backend when answer is saved
      return timeSpent;
    }
    return 0;
  };

  // Function to call ElevenLabs TTS API
  const textToSpeech = async (text) => {
    // Safety check: Don't call ElevenLabs if webspeech is configured
    if (voiceProvider !== 'elevenlabs') {
      console.log('Skipping ElevenLabs call - voiceProvider is set to:', voiceProvider);
      throw new Error('ElevenLabs not configured');
    }
    
    try {
      const response = await ttsAPI.speak(text);
      return response.data; // Axios returns data in .data property
    } catch (error) {
      console.error('Error calling TTS API:', error);
      throw error;
    }
  };

  // Fallback function for greeting using Web Speech API
  const fallbackGreeting = (greetingMessage, callback) => {
    const utterance = new SpeechSynthesisUtterance(greetingMessage);
    utterance.rate = 0.9;
    utterance.pitch = 1;
    utterance.volume = 1;
    
    utterance.onend = async () => {
      setIsSpeaking(false);
      await callback();
    };
    
    utterance.onerror = async (error) => {
      console.error('Speech synthesis error:', error);
      setIsSpeaking(false);
      await callback();
    };
    
    speechSynthesisRef.current = utterance;
    window.speechSynthesis.speak(utterance);
  };

  // Function to speak question - uses ElevenLabs or Web Speech API based on settings
  const speakQuestion = async (questionText, isFollowUp = false, onSpeechEnd = null) => {
    try {
      // Stop any ongoing speech
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current = null;
      }
      if (speechSynthesisRef.current) {
        window.speechSynthesis.cancel();
      }

      setIsSpeaking(true);

      if (voiceProvider === 'elevenlabs') {
        try {
          // Use ElevenLabs AI voice
          const audioBlob = await textToSpeech(questionText);
          const audioUrl = URL.createObjectURL(audioBlob);
          const audio = new Audio(audioUrl);
          currentAudioRef.current = audio;

          audio.onended = () => {
            setIsSpeaking(false);
            URL.revokeObjectURL(audioUrl);
            currentAudioRef.current = null;
            console.log('Finished speaking question (ElevenLabs)');
            // Call the callback after speech ends
            if (onSpeechEnd) {
              onSpeechEnd();
            }
          };

          audio.onerror = (error) => {
            console.error('Audio playback error:', error);
            setIsSpeaking(false);
            currentAudioRef.current = null;
            // Call callback even on error to ensure recognition starts
            if (onSpeechEnd) {
              onSpeechEnd();
            }
          };

          await audio.play();
          console.log('Started speaking question (ElevenLabs)');
        } catch (error) {
          console.error('ElevenLabs TTS failed, falling back to Web Speech API:', error);
          // Fallback to Web Speech API
          fallbackSpeech(questionText, onSpeechEnd);
        }
      } else {
        // Use Web Speech API
        fallbackSpeech(questionText, onSpeechEnd);
      }
    } catch (error) {
      console.error('Error in text-to-speech:', error);
      setIsSpeaking(false);
      // Call callback even on error to ensure recognition starts
      if (onSpeechEnd) {
        onSpeechEnd();
      }
    }
  };

  // Fallback function to use Web Speech API
  const fallbackSpeech = (questionText, onSpeechEnd = null) => {
    const utterance = new SpeechSynthesisUtterance(questionText);
    utterance.rate = 0.9;
    utterance.pitch = 1;
    utterance.volume = 1;

    utterance.onstart = () => {
      setIsSpeaking(true);
      console.log('Started speaking question (Web Speech)');
    };

    utterance.onend = () => {
      setIsSpeaking(false);
      console.log('Finished speaking question (Web Speech)');
      // Call the callback after speech ends
      if (onSpeechEnd) {
        onSpeechEnd();
      }
    };

    utterance.onerror = (error) => {
      console.error('Speech synthesis error:', error);
      setIsSpeaking(false);
      // Call callback even on error to ensure recognition starts
      if (onSpeechEnd) {
        onSpeechEnd();
      }
    };

    speechSynthesisRef.current = utterance;
    window.speechSynthesis.speak(utterance);
  };

  // Function to stop speaking
  // eslint-disable-next-line no-unused-vars
  const stopSpeaking = () => {
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    setIsSpeaking(false);
  };

  // Handle starting Ask Interviewer feature
  const handleStartAsking = () => {
    setIsAsking(true);
    setAskTranscript('');
    setAskInterimTranscript('');
    
    // Initialize speech recognition for asking
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const askRecognition = new SpeechRecognition();
      
      askRecognition.continuous = true;
      askRecognition.interimResults = true;
      askRecognition.lang = 'en-US';
      
      askRecognition.onresult = (event) => {
        let finalTranscript = '';
        let interimTranscript = '';
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript + ' ';
          } else {
            interimTranscript += transcript;
          }
        }
        
        if (finalTranscript) {
          setAskTranscript(prev => prev + finalTranscript);
        }
        setAskInterimTranscript(interimTranscript);
      };
      
      askRecognition.onerror = (event) => {
        console.error('Ask recognition error:', event.error);
        if (event.error !== 'no-speech') {
          setIsAsking(false);
        }
      };
      
      askRecognition.onend = () => {
        console.log('Ask recognition ended');
      };
      
      try {
        askRecognition.start();
        askRecognitionRef.current = askRecognition;
        console.log('Ask speech recognition started');
      } catch (error) {
        console.error('Error starting ask recognition:', error);
        setIsAsking(false);
      }
    } else {
      alert('Speech recognition is not supported in your browser.');
      setIsAsking(false);
    }
  };

  // Handle stopping Ask Interviewer feature and getting AI response
  const handleStopAsking = async () => {
    // Stop speech recognition
    if (askRecognitionRef.current) {
      try {
        askRecognitionRef.current.stop();
      } catch (e) {
        console.error('Error stopping ask recognition:', e);
      }
    }
    
    setIsAsking(false);
    
    // Wait a moment for final transcript
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Get the full question
    const fullQuestion = (askTranscript + ' ' + askInterimTranscript).trim();
    
    if (!fullQuestion) {
      alert('No question detected. Please try again.');
      return;
    }
    
    // Get current code
    const currentQuestion = interview.questions[currentQuestionIndex].questionId;
    const currentCode = isAnsweringFollowUp 
      ? followUpCodeAnswers[`${currentFollowUpParent}_${currentFollowUpIndex}`]
      : codeAnswers[currentQuestion._id]?.[selectedLanguage];
    
    // Call AI to get interviewer response
    setIsWaitingForAIResponse(true);
    
    try {
      const response = await interviewAPI.askInterviewer(interview._id, {
        question: fullQuestion,
        problemStatement: currentQuestion.question,
        code: currentCode || '',
        language: selectedLanguage,
        conversationHistory: askConversationHistory
      });
      
      const aiResponse = response.data.response;
      setAiInterviewerResponse(aiResponse);
      
      // Add to conversation history
      setAskConversationHistory(prev => [...prev, {
        question: fullQuestion,
        response: aiResponse
      }]);
      
      // Speak the response
      if (voiceProvider === 'elevenlabs') {
        try {
          const audioBlob = await textToSpeech(aiResponse);
          const audioUrl = URL.createObjectURL(audioBlob);
          const audio = new Audio(audioUrl);
          currentAudioRef.current = audio;
          
          audio.onended = () => {
            setIsSpeaking(false);
            currentAudioRef.current = null;
          };
          
          setIsSpeaking(true);
          await audio.play();
        } catch (error) {
          console.error('Error with ElevenLabs TTS:', error);
          // Fallback to Web Speech API
          const utterance = new SpeechSynthesisUtterance(aiResponse);
          utterance.onend = () => setIsSpeaking(false);
          setIsSpeaking(true);
          window.speechSynthesis.speak(utterance);
        }
      } else {
        // Use Web Speech API
        const utterance = new SpeechSynthesisUtterance(aiResponse);
        utterance.onend = () => setIsSpeaking(false);
        setIsSpeaking(true);
        window.speechSynthesis.speak(utterance);
      }
      
      // Clear ask transcript for next question
      setAskTranscript('');
      setAskInterimTranscript('');
      
    } catch (error) {
      console.error('Error getting AI interviewer response:', error);
      alert('Failed to get response. Please try again.');
    } finally {
      setIsWaitingForAIResponse(false);
    }
  };

  const handleNext = async () => {
    if (currentQuestionIndex < interview.questions.length - 1) {
      // Close any open transcript or code editor views
      setActiveView(null);
      
      setIsProcessing(true);
      
      try {
        const currentQuestionId = interview.questions[currentQuestionIndex].questionId._id;
        
        console.log('=== Starting Next Question Process ===');
        console.log('Current transcript before stop:', currentTranscript);
        console.log('Interim transcript:', interimTranscript);
        
        // Stop speech recognition immediately
        if (recognition) {
          try {
            recognition.stop();
            console.log('Speech recognition stopped');
          } catch (e) {
            console.error('Error stopping recognition:', e);
          }
        }
        
        setIsListening(false);
        setIsRecognitionReady(false);
        
        // Wait longer for all speech to be processed (3 seconds)
        console.log('Waiting for final speech processing...');
        await new Promise(resolve => setTimeout(resolve, 3000));
        
        // Capture any interim text that wasn't finalized
        let finalTranscript = currentTranscript.trim();
        if (interimTranscript.trim()) {
          finalTranscript += ' ' + interimTranscript.trim();
          console.log('Added interim transcript to final');
        }
        
        console.log('Final transcript to save:', finalTranscript);
        console.log('Transcript length:', finalTranscript.length, 'characters');
        
        // Check if this is a Coding question
        const currentQuestion = interview.questions[currentQuestionIndex].questionId;
        const isCodeQuestion = currentQuestion.questionType === 'Coding';
        const currentCode = isCodeQuestion ? codeAnswers[currentQuestionId]?.[selectedLanguage] : null;
        
        // Save transcript to transcripts object
        if (finalTranscript || isCodeQuestion) {
          if (!isCodeQuestion) {
            setTranscripts(prev => {
              const updated = {
                ...prev,
                [currentQuestionId]: finalTranscript
              };
              console.log('Updated transcripts:', updated);
              return updated;
            });
          }
          
          // Calculate time spent
          const timeSpent = saveQuestionTiming(currentQuestionId);
          
          // Save answer to backend
          console.log('Saving answer to backend...');
          const hasFollowUps = await handleAnswerChange(
            currentQuestionId, 
            finalTranscript || 'Code submission', 
            timeSpent,
            currentCode,
            isCodeQuestion ? selectedLanguage : null
          );
          
          // Wait for save confirmation
          await new Promise(resolve => setTimeout(resolve, 500));
          console.log('Answer saved successfully, hasFollowUps:', hasFollowUps);
          
          // If no follow-ups were generated, proceed to next question immediately
          if (!hasFollowUps) {
            console.log('No follow-ups generated, will proceed to next question');
          }
        } else {
          console.warn('No transcript to save for this question');
        }
        
        // Clear transcripts for next question
        setCurrentTranscript('');
        setInterimTranscript('');
        
        // Wait a bit to let state settle
        await new Promise(resolve => setTimeout(resolve, 200));
        
      } catch (error) {
        console.error('Error in handleNext:', error);
        alert('Error saving answer. Please try again.');
        setIsProcessing(false);
        setIsRecognitionReady(true); // Re-enable button on error
      } finally {
        // Don't set isProcessing to false yet - keep it true until speech recognition starts
        
        // After processing is complete, check if we need to proceed to next question
        setTimeout(() => {
          setIsAnsweringFollowUp(current => {
            console.log('Checking isAnsweringFollowUp:', current);
            if (!current) {
              // No follow-ups are being answered
              if (currentQuestionIndex < interview.questions.length - 1) {
                // Not the last question, proceed to next question
                console.log('No follow-ups active, proceeding to next question');
                proceedToNextQuestion();
              } else {
                // Last question and no follow-ups, auto-submit
                console.log('Last question with no follow-ups, auto-submitting');
                setIsProcessing(false);
                setIsRecognitionReady(true);
                setTimeout(() => {
                  handleSubmit();
                }, 500);
              }
            } else {
              console.log('Follow-ups are active, closing modal immediately');
              // Close processing modal immediately when follow-ups are ready
              setIsProcessing(false);
              setIsRecognitionReady(true);
              // Question will be spoken from handleAnswerChange after modal is closed
            }
            return current;
          });
        }, 100);
      }
    }
  };

  // Actually move to the next main question (called after follow-ups are done or if no follow-ups)
  const proceedToNextQuestion = async () => {
    // Move to next question
    const nextIndex = currentQuestionIndex + 1;
    console.log('Moving to question', nextIndex + 1);
    
    setCurrentQuestionIndex(nextIndex);
    setQuestionStartTime(Date.now());
    
    // Add chapter for next question when we actually start it
    if (videoStartTimeRef.current && interview.questions[nextIndex]) {
      const currentTimestamp = (Date.now() - videoStartTimeRef.current) / 1000;
      const newChapter = {
        questionText: interview.questions[nextIndex]?.questionId?.question || `Question ${nextIndex + 1}`,
        questionNumber: nextIndex + 1,
        isFollowUp: false,
        timestamp: Math.round(currentTimestamp)
      };
      
      // Check if this chapter already exists (avoid duplicates)
      const isDuplicate = chaptersRef.current.some(ch => 
        ch.questionNumber === newChapter.questionNumber && 
        ch.isFollowUp === false &&
        Math.abs(ch.timestamp - newChapter.timestamp) < 2 // Within 2 seconds
      );
      
      if (!isDuplicate) {
        chaptersRef.current.push(newChapter);
      }
    }
    
    // Clear transcripts for the new question
    setCurrentTranscript('');
    setInterimTranscript('');
    
    // Always set processing to false so user can continue
    setIsProcessing(false);
    
    // Wait 1.5 seconds before speaking question
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // Speak the question and start recognition after it finishes
    const nextQuestion = interview.questions[nextIndex]?.questionId;
    if (nextQuestion?.question) {
      // Skip reading if it's a Coding question, but still start recognition
      if (nextQuestion.questionType === 'Coding') {
        console.log('Skipping TTS for Coding question');
        // Start speech recognition immediately for coding questions
        if (recognition && !isSubmitting && started) {
          console.log('Starting speech recognition without TTS for Coding question');
          try {
            recognition.start();
            console.log('Speech recognition started successfully');
            setIsRecognitionReady(true);
            setIsListening(true);
          } catch (error) {
            console.error('Error starting speech recognition:', error);
            setIsRecognitionReady(true);
            // Continue without speech recognition - don't block the interview
          }
        }
      } else {
        // Read the question normally for non-Coding questions
        speakQuestion(nextQuestion.question, false, () => {
          // Start speech recognition after question finishes speaking
          if (recognition && !isSubmitting && started) {
            console.log('Starting speech recognition after next question was spoken');
            try {
              recognition.start();
              console.log('Speech recognition started successfully');
              setIsRecognitionReady(true);
              setIsListening(true);
            } catch (error) {
              console.error('Error starting speech recognition:', error);
              setIsRecognitionReady(true);
              // Continue without speech recognition - don't block the interview
            }
          }
        });
      }
    }
    
    console.log('=== Next Question Process Complete ===');
  };

  // Effect to handle moving to next question after follow-ups are done
  useEffect(() => {
    if (!isAnsweringFollowUp && !isProcessing && started && pendingFollowUps.length === 0 && currentFollowUpParent === null) {
      // Follow-ups are done, but don't auto-advance - this is handled by the UI
    }
  }, [isAnsweringFollowUp, isProcessing, started, pendingFollowUps.length, currentFollowUpParent]);

  // Debug effect to log state changes
  useEffect(() => {
    console.log('STATE UPDATE:', {
      isProcessing,
      isRecognitionReady,
      isAnsweringFollowUp,
      currentFollowUpIndex,
      pendingFollowUpsLength: pendingFollowUps.length,
      shouldShowQuestion: !isProcessing && (isRecognitionReady || isAnsweringFollowUp),
      shouldShowFollowUp: isAnsweringFollowUp && pendingFollowUps.length > 0
    });
  }, [isProcessing, isRecognitionReady, isAnsweringFollowUp, currentFollowUpIndex, pendingFollowUps.length]);

  const stopRecording = () => {
    return new Promise((resolve) => {
      if (mediaRecorder && isRecording) {
        mediaRecorder.onstop = () => {
          setIsRecording(false);
          resolve();
        };
        mediaRecorder.stop();
      } else {
        resolve();
      }
      
      if (recognition && isListening) {
        recognition.stop();
        setIsListening(false);
        setIsRecognitionReady(false);
      }
      
      if (mediaStream) {
        mediaStream.getTracks().forEach(track => track.stop());
      }
    });
  };

  const handleAutoSubmit = async (reason = 'timeout') => {
    setIsSubmitting(true);
    setTimeRemaining(0); // Stop the countdown timer
    await stopRecording();
    
    try {
      // Wait for final processing
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Save final question timing - only if we have a valid current question
      if (interview.questions[currentQuestionIndex]) {
        const currentQuestionId = interview.questions[currentQuestionIndex].questionId._id;
        const timeSpent = saveQuestionTiming(currentQuestionId);
        
        // Save final transcript including any interim text
        let finalTranscript = currentTranscript.trim();
        if (interimTranscript.trim()) {
          finalTranscript += ' ' + interimTranscript.trim();
        }
        
        if (finalTranscript) {
          setTranscripts(prev => ({
            ...prev,
            [currentQuestionId]: finalTranscript
          }));
          
          // Submit final answer without generating follow-ups (we're auto-submitting)
          try {
            await interviewAPI.submitAnswer(id, {
              questionId: currentQuestionId,
              answer: finalTranscript,
              timeSpent: timeSpent
            });
            console.log('Final answer saved (auto-submit)');
          } catch (error) {
            console.error('Error saving final answer:', error);
          }
        }
      } else {
        console.log('No current question to save (auto-submit)');
      }
      
      console.log('Auto-submitting interview...');
      await interviewAPI.submitInterview(id);
      console.log('Auto-submit successful');
      
      // Exit fullscreen after auto-submission
      try {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) { /* Safari */
          await document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) { /* IE11 */
          await document.msExitFullscreen();
        }
      } catch (error) {
        console.log('Exit fullscreen failed:', error);
      }
      
      // Show different messages based on the reason for auto-submission
      if (reason === 'tab-switch') {
        alert('You switched tabs or minimized the window. Your interview has been automatically submitted.');
      } else {
        alert('Time is up! Interview has been automatically submitted.');
      }
      navigate('/my-interviews');
    } catch (error) {
      console.error('Error auto-submitting interview:', error);
      console.error('Error stack:', error.stack);
      console.error('Error response:', error.response);
      alert('Failed to auto-submit interview: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleSubmit = async () => {
    console.log('Submit button clicked');
    console.log('State check:', {
      isProcessing,
      isRecognitionReady,
      isGeneratingFollowUp,
      isSubmitting
    });

    // Exit fullscreen before showing confirmation dialog
    // This prevents browser from blocking the dialog in fullscreen mode
    try {
      if (document.fullscreenElement) {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) { /* Safari */
          await document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) { /* IE11 */
          await document.msExitFullscreen();
        }
      }
    } catch (error) {
      console.log('Exit fullscreen failed:', error);
    }

    // Show confirmation dialog
    if (!window.confirm('Are you sure you want to submit the interview?')) {
      // If user cancels, re-enter fullscreen
      try {
        const elem = document.documentElement;
        if (elem.requestFullscreen) {
          await elem.requestFullscreen();
        } else if (elem.webkitRequestFullscreen) { /* Safari */
          await elem.webkitRequestFullscreen();
        } else if (elem.msRequestFullscreen) { /* IE11 */
          await elem.msRequestFullscreen();
        }
      } catch (error) {
        console.log('Re-enter fullscreen failed:', error);
      }
      return;
    }

    setIsSubmitting(true);
    setTimeRemaining(0); // Stop the countdown timer

    try {
      console.log('Starting submission process...');
      
      // Wait for speech recognition to finish processing
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Wait for recording to stop
      await stopRecording();
      
      // Save final question timing - only if we have a valid current question
      if (interview.questions[currentQuestionIndex]) {
        const currentQuestionId = interview.questions[currentQuestionIndex].questionId._id;
        const timeSpent = saveQuestionTiming(currentQuestionId);
        
        // Save final transcript including any interim text
        let finalTranscript = currentTranscript.trim();
        if (interimTranscript.trim()) {
          finalTranscript += ' ' + interimTranscript.trim();
        }
        
        if (finalTranscript) {
          setTranscripts(prev => ({
            ...prev,
            [currentQuestionId]: finalTranscript
          }));
          
          // Submit final answer without generating follow-ups (we're submitting)
          try {
            await interviewAPI.submitAnswer(id, {
              questionId: currentQuestionId,
              answer: finalTranscript,
              timeSpent: timeSpent
            });
            console.log('Final answer saved');
          } catch (error) {
            console.error('Error saving final answer:', error);
          }
        }
      } else {
        console.log('No current question to save (already at end or invalid index)');
      }
      
      console.log('Answers saved, preparing to submit interview...');
      
      // Small delay to ensure all chunks are collected
      await new Promise(resolve => setTimeout(resolve, 500));
      
      console.log('Checking video recording requirements...');
      console.log('requireVideoRecording:', interview.requireVideoRecording);
      console.log('recordedChunks length:', recordedChunksRef.current.length);
      
      // Upload video recording to S3 directly from frontend - only if required
      if (interview.requireVideoRecording !== false && recordedChunksRef.current.length > 0) {
        const blob = new Blob(recordedChunksRef.current, { type: 'video/webm' });
        console.log('Uploading video to S3, size:', blob.size, 'bytes');
        console.log('Video chapters:', chaptersRef.current);
        
        try {
          setIsUploading(true);
          setUploadProgress(0);
          setUploadStatus(null);
          setUploadError('');
          
          console.log('Step 1: Requesting S3 presigned URL...');
          
          // Step 1: Get presigned URL from backend
          const filename = `interview-${id}-${Date.now()}.webm`;
          const s3UrlResponse = await interviewAPI.getS3UploadUrl(id, filename, 'video/webm');
          const { presignedUrl, s3Url } = s3UrlResponse.data;
          
          console.log('✓ Presigned URL received');
          console.log('File size:', (blob.size / 1024 / 1024).toFixed(2), 'MB');
          
          // Step 2: Upload directly to S3 using presigned URL
          console.log('Step 2: Uploading to S3...');
          await interviewAPI.uploadVideoToS3(presignedUrl, blob, (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
            console.log(`Upload progress: ${progressEvent.loaded}/${progressEvent.total} bytes (${percentCompleted}%)`);
          });
          
          console.log('✓ Video uploaded to S3 successfully');
          
          // Step 3: Save S3 URL to database
          console.log('Step 3: Saving S3 URL to database...');
          await interviewAPI.saveVideoUrl(id, {
            s3Url: s3Url,
            chapters: chaptersRef.current
          });
          
          setUploadProgress(100);
          setUploadStatus('success');
          console.log('✓ S3 URL saved successfully');
          
          // Keep success message visible for 1 second
          await new Promise(resolve => setTimeout(resolve, 1000));
          setIsUploading(false);
          
          console.log('Video upload completed successfully');
        } catch (videoError) {
          console.error('Error during video upload process:', videoError);
          console.error('Error details:', {
            message: videoError.message,
            response: videoError.response?.data
          });
          setUploadStatus('error');
          setUploadError(videoError.response?.data?.message || videoError.message || 'Video upload failed');
          
          // Show error modal for 3 seconds
          await new Promise(resolve => setTimeout(resolve, 3000));
          setIsUploading(false);
          
          // Ask user if they want to continue without video
          const continueWithoutVideo = window.confirm(
            'Video upload failed. Do you want to submit the interview without video?\n\n' +
            'Error: ' + (videoError.response?.data?.message || videoError.message || 'Unknown error')
          );
          
          if (!continueWithoutVideo) {
            setIsSubmitting(false);
            return;
          }
        }
      } else {
        console.log('Video upload skipped (not required or no recording)');
      }
      
      // Submit the interview after video upload completes
      console.log('Submitting interview to backend...');
      const submitResponse = await interviewAPI.submitInterview(id);
      console.log('Interview submitted successfully');
      
      // Clear localStorage after successful submission
      try {
        const storageKey = `interview_${id}_code`;
        localStorage.removeItem(storageKey);
        console.log('Cleared code from localStorage after submission');
      } catch (error) {
        console.error('Error clearing localStorage:', error);
      }
      
      // Display the message from backend which includes info about AI analysis and email
      const successMessage = submitResponse.data?.message || 'Interview submitted successfully!';
      alert(successMessage);
      navigate('/my-interviews');
    } catch (error) {
      console.error('Error submitting interview:', error);
      console.error('Error stack:', error.stack);
      console.error('Error response:', error.response);
      alert('Failed to submit interview: ' + (error.response?.data?.message || error.message));
      setIsSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>Loading interview...</div>;
  }

  if (!interview) {
    return <div style={{ textAlign: 'center', padding: '40px', color: '#e53e3e' }}>Interview not found</div>;
  }

  if (!started) {
    return (
      <>
        <style>{pulseKeyframes}</style>
        <div className="container">
          <div className="card" style={{ maxWidth: '800px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              <h1 style={{ color: '#667eea', fontSize: '28px', marginBottom: '12px' }}>{interview.title}</h1>
              {interview.description && <p style={{ color: '#718096', fontSize: '15px', margin: 0 }}>{interview.description}</p>}
            </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px', padding: '20px', background: '#f7fafc', borderRadius: '8px' }}>
            <div style={{ textAlign: 'center' }}>
              <strong style={{ display: 'block', marginBottom: '4px', color: '#718096', fontSize: '12px' }}>DURATION</strong>
              <span style={{ color: '#2d3748', fontSize: '16px', fontWeight: 600 }}>{interview.duration} min</span>
            </div>
            <div style={{ textAlign: 'center' }}>
              <strong style={{ display: 'block', marginBottom: '4px', color: '#718096', fontSize: '12px' }}>QUESTIONS</strong>
              <span style={{ color: '#2d3748', fontSize: '16px', fontWeight: 600 }}>{interview.questions.length}</span>
            </div>
            <div style={{ textAlign: 'center' }}>
              <strong style={{ display: 'block', marginBottom: '4px', color: '#718096', fontSize: '12px' }}>END TIME</strong>
              <span style={{ color: '#2d3748', fontSize: '14px', fontWeight: 600 }}>{new Date(interview.endDate).toLocaleString()}</span>
            </div>
          </div>

          {interview.instructions && (
            <div style={{ marginBottom: '24px', padding: '16px', background: '#e7f3ff', borderRadius: '8px', borderLeft: '4px solid #667eea' }}>
              <h3 style={{ color: '#2c5282', fontSize: '16px', marginBottom: '8px' }}>Instructions:</h3>
              <p style={{ color: '#2d3748', fontSize: '14px', margin: 0, lineHeight: 1.6 }}>{interview.instructions}</p>
            </div>
          )}

          <div style={{ marginBottom: '32px', padding: '20px', background: '#fffaf0', border: '1px solid #fbd38d', borderRadius: '8px' }}>
            <h3 style={{ color: '#744210', fontSize: '16px', marginBottom: '12px' }}>⚠️ Important Notes:</h3>
            <ul style={{ margin: 0, paddingLeft: '24px', color: '#744210', fontSize: '14px', lineHeight: 1.8 }}>
              <li>You have {interview.duration} minutes to complete this interview</li>
              {interview.requireVideoRecording !== false && (
                <>
                  <li>📹 Your interview will be recorded via webcam</li>
                  <li>🎤 Your speech will be converted to text automatically</li>
                  <li>Please allow camera and microphone access when prompted</li>
                </>
              )}
              {interview.requireVideoRecording === false && (
                <li>🎤 Speech-to-text will be used to capture your responses</li>
              )}
              <li>Timer will start when you click "Start Interview"</li>
              <li>Interview will auto-submit when time runs out</li>
              <li>You cannot modify answers after submission</li>
            </ul>
          </div>

          <div style={{ marginBottom: '32px', padding: '20px', background: '#f0fff4', border: '1px solid #9ae6b4', borderRadius: '8px' }}>
            <h3 style={{ color: '#22543d', fontSize: '16px', marginBottom: '12px' }}>💡 Environment & Etiquette:</h3>
            <ul style={{ margin: 0, paddingLeft: '24px', color: '#22543d', fontSize: '14px', lineHeight: 1.8 }}>
              <li><strong>Quiet Space:</strong> Choose a quiet room with minimal background noise and distractions</li>
              <li><strong>Good Lighting:</strong> Ensure your face is well-lit (natural light or a lamp in front of you)</li>
              <li><strong>Stable Internet:</strong> Use a reliable internet connection to avoid disconnections</li>
              <li><strong>Professional Attire:</strong> Dress appropriately as you would for an in-person interview</li>
              <li><strong>Camera Position:</strong> Position camera at eye level with your face clearly visible</li>
              <li><strong>Eye Contact:</strong> Look at the camera when speaking to simulate eye contact</li>
              <li><strong>Clear Speech:</strong> Speak clearly and at a moderate pace for accurate transcription</li>
              <li><strong>Body Language:</strong> Sit upright and maintain professional posture throughout</li>
            </ul>
          </div>

          <button 
            onClick={handleStart} 
            className="btn btn-primary" 
            style={{ 
              width: '100%', 
              padding: '14px', 
              fontSize: '16px',
              opacity: isStarting ? 0.6 : 1,
              cursor: isStarting ? 'not-allowed' : 'pointer'
            }}
            disabled={isStarting}
          >
            {isStarting ? 'Starting...' : 'Start Interview'}
          </button>
        </div>
      </div>
      </>
    );
  }

  const currentQuestion = interview.questions[currentQuestionIndex]?.questionId;

  return (
    <>
      <style>{pulseKeyframes}</style>
      <div className="container">
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', paddingBottom: '16px', borderBottom: '2px solid #e2e8f0' }}>
            <div>
              <h2 style={{ color: '#667eea', fontSize: '20px', marginBottom: '4px' }}>{interview.title}</h2>
              <div style={{ color: '#718096', fontSize: '13px' }}>
                Question {currentQuestionIndex + 1} of {interview.questions.length}
              </div>
            </div>
            <div style={{
              padding: '12px 20px',
            background: timeRemaining < 300 ? '#fed7d7' : '#e7f3ff',
            color: timeRemaining < 300 ? '#742a2a' : '#2c5282',
            borderRadius: '8px',
            fontSize: '18px',
            fontWeight: 700,
            border: timeRemaining < 300 ? '2px solid #fc8181' : '2px solid #667eea'
          }}>
            ⏱️ {formatTime(timeRemaining)}
          </div>
        </div>

        <div style={{ marginBottom: '32px', padding: '24px', background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
          {currentQuestion && (
            <>
              {/* Show skeleton loader when processing */}
              {isProcessing && (
                <div>
                  <style>{`
                    @keyframes shimmer {
                      0% { background-position: -1000px 0; }
                      100% { background-position: 1000px 0; }
                    }
                    @keyframes fadeIn {
                      from { opacity: 0; transform: translateY(-10px); }
                      to { opacity: 1; transform: translateY(0); }
                    }
                  `}</style>
                  
                  {/* Coaching Message */}
                  <div style={{ 
                    marginBottom: '28px', 
                    padding: '20px 24px', 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: '12px',
                    textAlign: 'center',
                    animation: 'fadeIn 0.5s ease-out'
                  }}>
                    <div style={{ 
                      fontSize: '24px', 
                      marginBottom: '8px',
                      animation: 'pulse 2s ease-in-out infinite'
                    }}>✨</div>
                    <h3 style={{ 
                      color: 'white', 
                      fontSize: '18px', 
                      fontWeight: 700, 
                      margin: '0 0 8px 0',
                      letterSpacing: '0.3px'
                    }}>
                      Great job! Processing your response...
                    </h3>
                    <p style={{ 
                      color: 'rgba(255, 255, 255, 0.9)', 
                      fontSize: '14px', 
                      margin: 0,
                      fontWeight: 500
                    }}>
                      Take a deep breath. You're doing amazing! 💪
                    </p>
                  </div>

                  {/* Badges skeleton */}
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                    <div style={{ 
                      width: '80px', 
                      height: '28px', 
                      borderRadius: '20px', 
                      background: 'linear-gradient(90deg, #f0f0f0 0%, #e0e0e0 50%, #f0f0f0 100%)',
                      backgroundSize: '1000px 100%',
                      animation: 'shimmer 2s infinite'
                    }}></div>
                    <div style={{ 
                      width: '100px', 
                      height: '28px', 
                      borderRadius: '20px', 
                      background: 'linear-gradient(90deg, #f0f0f0 0%, #e0e0e0 50%, #f0f0f0 100%)',
                      backgroundSize: '1000px 100%',
                      animation: 'shimmer 2s infinite'
                    }}></div>
                  </div>

                  {/* Question title skeleton */}
                  <div style={{ marginBottom: '24px' }}>
                    <div style={{ 
                      width: '140px', 
                      height: '24px', 
                      borderRadius: '4px', 
                      background: 'linear-gradient(90deg, #f0f0f0 0%, #e0e0e0 50%, #f0f0f0 100%)',
                      backgroundSize: '1000px 100%',
                      animation: 'shimmer 2s infinite',
                      marginBottom: '12px'
                    }}></div>
                    <div style={{ 
                      width: '100%', 
                      height: '20px', 
                      borderRadius: '4px', 
                      background: 'linear-gradient(90deg, #f0f0f0 0%, #e0e0e0 50%, #f0f0f0 100%)',
                      backgroundSize: '1000px 100%',
                      animation: 'shimmer 2s infinite',
                      marginBottom: '8px'
                    }}></div>
                    <div style={{ 
                      width: '85%', 
                      height: '20px', 
                      borderRadius: '4px', 
                      background: 'linear-gradient(90deg, #f0f0f0 0%, #e0e0e0 50%, #f0f0f0 100%)',
                      backgroundSize: '1000px 100%',
                      animation: 'shimmer 2s infinite'
                    }}></div>
                  </div>

                  {/* Recording indicator skeleton */}
                  <div style={{ marginBottom: '20px', padding: '16px', background: '#f7fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <div style={{ 
                      width: '200px', 
                      height: '32px', 
                      borderRadius: '12px', 
                      background: 'linear-gradient(90deg, #f0f0f0 0%, #e0e0e0 50%, #f0f0f0 100%)',
                      backgroundSize: '1000px 100%',
                      animation: 'shimmer 2s infinite',
                      margin: '0 auto 12px'
                    }}></div>
                    <div style={{ 
                      width: '90%', 
                      height: '16px', 
                      borderRadius: '4px', 
                      background: 'linear-gradient(90deg, #f0f0f0 0%, #e0e0e0 50%, #f0f0f0 100%)',
                      backgroundSize: '1000px 100%',
                      animation: 'shimmer 2s infinite',
                      margin: '0 auto'
                    }}></div>
                  </div>
                </div>
              )}

              {/* Show question when not processing */}
              {!isProcessing && (
                <>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                    <span style={{
                      padding: '6px 14px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: 600,
                      background: currentQuestion.difficulty === 'Easy' ? '#c6f6d5' : currentQuestion.difficulty === 'Medium' ? '#feebc8' : '#fed7d7',
                      color: currentQuestion.difficulty === 'Easy' ? '#22543d' : currentQuestion.difficulty === 'Medium' ? '#744210' : '#742a2a'
                    }}>
                      {currentQuestion.difficulty}
                    </span>
                    <span style={{ padding: '6px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: 600, background: '#bee3f8', color: '#2c5282' }}>
                      {currentQuestion.questionType}
                    </span>
                    {isSpeaking && (
                      <span style={{ 
                        padding: '6px 14px', 
                        borderRadius: '20px', 
                        fontSize: '12px', 
                        fontWeight: 600, 
                        background: '#d6bcfa', 
                        color: '#44337a',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <span style={{ animation: 'pulse 1s ease-in-out infinite' }}>🔊</span>
                        Reading question...
                      </span>
                    )}
                  </div>

                  {/* Recording/Listening Indicators - Moved to Top */}
                  {(currentQuestion.questionType === 'OpenEnded' || 
                    currentQuestion.questionType === 'FillInBlank' || 
                    currentQuestion.questionType === 'CodeSnippet' ||
                    currentQuestion.questionType === 'Coding') && (
                    <div style={{ marginBottom: '20px', padding: '16px', background: '#f7fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'center' }}>
                        {isRecording && (
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '8px', 
                            padding: '8px 16px', 
                            background: '#fed7d7', 
                            borderRadius: '12px'
                          }}>
                            <div style={{ 
                              width: '10px', 
                              height: '10px', 
                              borderRadius: '50%', 
                              background: '#e53e3e', 
                              animation: 'pulse 1s ease-in-out infinite' 
                            }}></div>
                            <span style={{ color: '#742a2a', fontSize: '14px', fontWeight: 600 }}>Recording</span>
                          </div>
                        )}
                        {isListening && (
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '8px', 
                            padding: '8px 16px', 
                            background: '#c6f6d5', 
                            borderRadius: '12px'
                          }}>
                            <span style={{ fontSize: '14px', fontWeight: 600, animation: 'pulse 1s ease-in-out infinite' }}>🎤</span>
                            <span style={{ color: '#22543d', fontSize: '14px', fontWeight: 600 }}>Listening</span>
                          </div>
                        )}
                        {!isListening && !isProcessing && !isSubmitting && (
                          <button
                            onClick={handleManualRestartRecognition}
                            style={{
                              padding: '8px 16px',
                              background: '#ed8936',
                              color: 'white',
                              border: 'none',
                              borderRadius: '12px',
                              fontSize: '14px',
                              fontWeight: 600,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => e.target.style.background = '#dd6b20'}
                            onMouseLeave={(e) => e.target.style.background = '#ed8936'}
                          >
                            <span style={{ fontSize: '14px' }}>🎤</span>
                            <span>Restart Voice Recognition</span>
                          </button>
                        )}
                      </div>
                      <p style={{ textAlign: 'center', color: '#718096', fontSize: '14px', margin: '12px 0 0 0', fontStyle: 'italic' }}>
                        💡 Speak your answer clearly - your speech is being recorded and converted to text
                      </p>
                    </div>
                  )}

                  {/* Question Display - Main Question or Follow-up */}
                  {!isAnsweringFollowUp ? (
                    <div style={{ marginBottom: '24px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <h2 style={{ color: '#2d3748', fontSize: '20px', fontWeight: 700, margin: 0 }}>Question {currentQuestionIndex + 1}</h2>
                          <ReportIssueButton
                            sourceType="quiz"
                            sourceId={interview._id}
                            questionPath={`questions.${currentQuestionIndex}`}
                            questionId={currentQuestion._id}
                            questionIndex={currentQuestionIndex}
                            questionData={{
                              question: currentQuestion.question,
                              questionType: currentQuestion.questionType,
                              options: currentQuestion.options,
                              correctAnswer: currentQuestion.correctAnswer,
                              difficulty: currentQuestion.difficulty,
                              tags: currentQuestion.tags,
                              category: currentQuestion.category
                            }}
                            metadata={{
                              sourceName: interview.title,
                              sourceCode: interview.code,
                              questionNumber: currentQuestionIndex + 1,
                              totalQuestions: interview.questions?.length || 0,
                              interviewId: interview._id,
                              reportedFrom: 'interview-taking',
                              timestamp: new Date().toISOString(),
                              groupName: interview.group?.name,
                              groupId: interview.group?._id
                            }}
                            size="icon-only"
                          />
                        </div>
                        {/* Hide Replay button for Coding questions since they're not read aloud */}
                        {currentQuestion.questionType !== 'Coding' && (
                          <button
                            onClick={() => speakQuestion(currentQuestion.question, false)}
                            style={{
                              padding: '6px 12px',
                              background: '#667eea',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: 600,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}
                            disabled={isSpeaking}
                          >
                            🔊 {isSpeaking ? 'Speaking...' : 'Replay Question'}
                          </button>
                        )}
                      </div>
                      <p style={{ color: '#2d3748', fontSize: '16px', lineHeight: 1.6, margin: 0 }}>{currentQuestion.question}</p>
                    </div>
                  ) : (
                    pendingFollowUps.length > 0 && (
                      <div style={{ 
                        marginBottom: '24px', 
                        padding: '20px', 
                        background: currentFollowUpMetadata?.isNested ? '#fff5f7' : '#faf5ff', 
                        borderRadius: '12px', 
                        border: currentFollowUpMetadata?.isNested ? '2px solid #d53f8c' : '2px solid #9f7aea' 
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', justifyContent: 'space-between' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <span style={{ 
                                padding: '6px 12px', 
                                borderRadius: '20px', 
                                fontSize: '12px', 
                                fontWeight: 700, 
                                background: currentFollowUpMetadata?.isNested ? '#d53f8c' : '#9f7aea', 
                                color: 'white' 
                              }}>
                                {currentFollowUpMetadata?.isNested ? '🔄 Nested Follow-up' : '🤖 AI Follow-up'} {currentFollowUpIndex + 1} of {pendingFollowUps.length}
                              </span>
                              <span style={{ fontSize: '12px', color: currentFollowUpMetadata?.isNested ? '#97266d' : '#6b46c1', fontWeight: 500 }}>
                                Based on your previous answer
                              </span>
                            </div>
                            {currentFollowUpMetadata?.isNested && currentFollowUpMetadata?.parentFollowUpQuestion && (
                              <div style={{ 
                                padding: '8px 12px', 
                                background: '#fed7e2', 
                                borderRadius: '6px', 
                                fontSize: '11px', 
                                color: '#702459',
                                fontStyle: 'italic',
                                maxWidth: '600px'
                              }}>
                                💡 This is a follow-up to your follow-up answer: "{currentFollowUpMetadata.parentFollowUpQuestion.substring(0, 80)}{currentFollowUpMetadata.parentFollowUpQuestion.length > 80 ? '...' : ''}"
                              </div>
                            )}
                          </div>
                          <button
                            onClick={() => speakQuestion(pendingFollowUps[currentFollowUpIndex]?.question, true)}
                            style={{
                              padding: '6px 12px',
                              background: currentFollowUpMetadata?.isNested ? '#d53f8c' : '#9f7aea',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: 600,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}
                            disabled={isSpeaking}
                          >
                            🔊 {isSpeaking ? 'Speaking...' : 'Replay'}
                          </button>
                        </div>
                        <h2 style={{ color: '#44337a', fontSize: '20px', lineHeight: 1.5, fontWeight: 700, margin: 0 }}>
                          {pendingFollowUps[currentFollowUpIndex]?.question}
                        </h2>
                        
                        {/* Display previous code if this is a follow-up on a coding question */}
                        {(() => {
                          // Check if parent question was a coding question or previous follow-up had code
                          const parentQuestion = interview.questions.find(q => q.questionId._id === currentFollowUpParent)?.questionId;
                          const previousCode = parentQuestion?.questionType === 'Coding' 
                            ? codeAnswers[currentFollowUpParent]?.[selectedLanguage] 
                            : null;
                          
                          // Check if there was code in a previous follow-up
                          const previousFollowUpIndex = currentFollowUpIndex > 0 ? currentFollowUpIndex - 1 : -1;
                          const previousFollowUpCodeKey = previousFollowUpIndex >= 0 
                            ? `${currentFollowUpParent}_${previousFollowUpIndex}` 
                            : null;
                          const previousFollowUpCode = previousFollowUpCodeKey && followUpCodeAnswers[previousFollowUpCodeKey];
                          
                          const displayCode = previousFollowUpCode || previousCode;
                          
                          if (displayCode && displayCode.trim() !== '' && displayCode.trim() !== '// Write your code here...\n') {
                            return (
                              <div style={{ marginTop: '16px' }}>
                                <details style={{ 
                                  background: '#f8fafc', 
                                  border: '1px solid #e2e8f0', 
                                  borderRadius: '8px',
                                  padding: '12px'
                                }}>
                                  <summary style={{ 
                                    cursor: 'pointer', 
                                    fontWeight: 600, 
                                    fontSize: '14px', 
                                    color: '#4a5568',
                                    userSelect: 'none'
                                  }}>
                                    💻 Your Previous Code
                                  </summary>
                                  <div style={{ 
                                    marginTop: '12px', 
                                    border: '1px solid #e2e8f0', 
                                    borderRadius: '6px', 
                                    overflow: 'hidden',
                                    maxHeight: '300px'
                                  }}>
                                    <Editor
                                      height="300px"
                                      language={selectedLanguage === 'csharp' ? 'csharp' : selectedLanguage}
                                      theme={editorTheme}
                                      value={displayCode}
                                      options={{
                                        readOnly: true,
                                        fontSize: 13,
                                        minimap: { enabled: false },
                                        scrollBeyondLastLine: false,
                                        wordWrap: 'on',
                                        fontFamily: 'Consolas, Monaco, "Courier New", monospace'
                                      }}
                                    />
                                  </div>
                                </details>
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    )
                  )}

                  {/* View Toggle Buttons - Show below question */}
                  {started && !loading && (
                    <div style={{
                      marginTop: '20px',
                      marginBottom: '24px',
                      display: 'flex',
                      gap: '12px',
                      padding: '16px',
                      background: '#f7fafc',
                      borderRadius: '12px',
                      border: '1px solid #e2e8f0'
                    }}>
                      <button
                        onClick={() => setActiveView(activeView === 'text' ? null : 'text')}
                        style={{
                          flex: 1,
                          padding: '12px 20px',
                          background: activeView === 'text' ? 'linear-gradient(135deg, #667eea, #764ba2)' : 'white',
                          color: activeView === 'text' ? 'white' : '#4a5568',
                          border: activeView === 'text' ? 'none' : '2px solid #e2e8f0',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: 600,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                          transition: 'all 0.2s',
                          boxShadow: activeView === 'text' ? '0 4px 12px rgba(102, 126, 234, 0.3)' : 'none'
                        }}
                        onMouseEnter={(e) => {
                          if (activeView !== 'text') {
                            e.target.style.borderColor = '#cbd5e0';
                            e.target.style.background = '#f7fafc';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (activeView !== 'text') {
                            e.target.style.borderColor = '#e2e8f0';
                            e.target.style.background = 'white';
                          }
                        }}
                      >
                        <span style={{ fontSize: '16px' }}>📝</span>
                        <span>View Text Transcript</span>
                        {activeView === 'text' && <span style={{ fontSize: '12px' }}>✓</span>}
                      </button>
                      
                      {/* Hide Code Editor button for Coding questions (they already have inline editor) */}
                      {!(currentQuestion?.questionType === 'Coding' || (isAnsweringFollowUp && pendingFollowUps[currentFollowUpIndex]?.requiresCode)) && (
                        <button
                          onClick={() => setActiveView(activeView === 'code' ? null : 'code')}
                          style={{
                            flex: 1,
                            padding: '12px 20px',
                            background: activeView === 'code' ? 'linear-gradient(135deg, #48bb78, #38a169)' : 'white',
                            color: activeView === 'code' ? 'white' : '#4a5568',
                            border: activeView === 'code' ? 'none' : '2px solid #e2e8f0',
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            transition: 'all 0.2s',
                            boxShadow: activeView === 'code' ? '0 4px 12px rgba(72, 187, 120, 0.3)' : 'none'
                          }}
                          onMouseEnter={(e) => {
                            if (activeView !== 'code') {
                              e.target.style.borderColor = '#cbd5e0';
                              e.target.style.background = '#f7fafc';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (activeView !== 'code') {
                              e.target.style.borderColor = '#e2e8f0';
                              e.target.style.background = 'white';
                            }
                          }}
                        >
                          <span style={{ fontSize: '16px' }}>💻</span>
                          <span>Code Editor</span>
                          {activeView === 'code' && <span style={{ fontSize: '12px' }}>✓</span>}
                        </button>
                      )}
                    </div>
                  )}

                  {/* View Text Content */}
                  {activeView === 'text' && (
                    <div style={{
                      marginBottom: '24px',
                      padding: '20px',
                      background: 'linear-gradient(135deg, #f0f4ff, #e9ecff)',
                      borderRadius: '12px',
                      border: '2px solid #667eea',
                      minHeight: '200px'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '16px'
                      }}>
                        <h3 style={{
                          margin: 0,
                          fontSize: '16px',
                          fontWeight: 700,
                          color: '#4c51bf',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <span>🎙️</span>
                          <span>Live Speech-to-Text Transcript</span>
                        </h3>
                        {isListening && (
                          <div style={{
                            padding: '6px 12px',
                            background: '#ef4444',
                            color: 'white',
                            borderRadius: '20px',
                            fontSize: '12px',
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}>
                            <div style={{
                              width: '8px',
                              height: '8px',
                              background: 'white',
                              borderRadius: '50%',
                              animation: 'pulse 1.5s infinite'
                            }} />
                            Recording
                          </div>
                        )}
                      </div>
                      
                      <div style={{
                        background: 'white',
                        borderRadius: '8px',
                        padding: '16px',
                        minHeight: '150px',
                        maxHeight: '400px',
                        overflowY: 'auto',
                        fontFamily: 'system-ui, -apple-system, sans-serif',
                        fontSize: '15px',
                        lineHeight: '1.6',
                        color: '#2d3748',
                        border: '1px solid #cbd5e0'
                      }}>
                        {currentTranscript || interimTranscript ? (
                          <>
                            <span>{currentTranscript}</span>
                            {interimTranscript && (
                              <span style={{ color: '#a0aec0', fontStyle: 'italic' }}>
                                {currentTranscript && ' '}{interimTranscript}
                              </span>
                            )}
                          </>
                        ) : (
                          <p style={{
                            margin: 0,
                            color: '#a0aec0',
                            fontStyle: 'italic',
                            textAlign: 'center',
                            paddingTop: '40px'
                          }}>
                            {isListening ? 'Listening... Start speaking to see your transcript here.' : 'No transcript available yet. Start speech recognition to begin.'}
                          </p>
                        )}
                      </div>
                      
                      <div style={{
                        marginTop: '12px',
                        padding: '10px 12px',
                        background: '#ebf4ff',
                        borderRadius: '6px',
                        fontSize: '12px',
                        color: '#2c5282',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <span>💡</span>
                        <span>
                          <strong>Word count:</strong> {(currentTranscript + ' ' + interimTranscript).trim().split(/\s+/).filter(w => w.length > 0).length} words
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Code Editor View */}
                  {activeView === 'code' && (
                    <div style={{
                      marginBottom: '24px',
                      padding: '20px',
                      background: 'linear-gradient(135deg, #f0fff4, #e6ffef)',
                      borderRadius: '12px',
                      border: '2px solid #48bb78'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '16px'
                      }}>
                        <h3 style={{
                          margin: 0,
                          fontSize: '16px',
                          fontWeight: 700,
                          color: '#22543d',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <span>💻</span>
                          <span>Code Editor</span>
                        </h3>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                          <select
                            value={selectedLanguage}
                            onChange={(e) => setSelectedLanguage(e.target.value)}
                            style={{
                              padding: '6px 10px',
                              border: '1px solid #c6f6d5',
                              borderRadius: '6px',
                              fontSize: '13px',
                              backgroundColor: 'white',
                              cursor: 'pointer',
                              outline: 'none',
                              fontWeight: 500
                            }}
                          >
                            <option value="javascript">JavaScript</option>
                            <option value="python">Python</option>
                            <option value="java">Java</option>
                            <option value="sql">SQL</option>
                            <option value="csharp">C#</option>
                            <option value="typescript">TypeScript</option>
                          </select>
                          <button
                            onClick={() => setEditorTheme(editorTheme === 'vs-dark' ? 'light' : 'vs-dark')}
                            style={{
                              padding: '6px 10px',
                              border: '1px solid #c6f6d5',
                              borderRadius: '6px',
                              background: 'white',
                              cursor: 'pointer',
                              fontSize: '13px',
                              fontWeight: 500,
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                          >
                            {editorTheme === 'vs-dark' ? '☀️' : '🌙'}
                          </button>
                        </div>
                      </div>
                      
                      <div style={{
                        border: '1px solid #c6f6d5',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        height: '500px'
                      }}>
                        <Editor
                          height="100%"
                          language={selectedLanguage === 'csharp' ? 'csharp' : selectedLanguage}
                          theme={editorTheme}
                          value={
                            isAnsweringFollowUp 
                              ? (followUpCodeAnswers[`${currentFollowUpParent}_${currentFollowUpIndex}`] || '// Write your code here...\n')
                              : (codeAnswers[currentQuestion?._id]?.[selectedLanguage] || '// Write your code here...\n')
                          }
                          onChange={(value) => {
                            if (isAnsweringFollowUp) {
                              const key = `${currentFollowUpParent}_${currentFollowUpIndex}`;
                              setFollowUpCodeAnswers(prev => ({
                                ...prev,
                                [key]: value
                              }));
                            } else {
                              setCodeAnswers(prev => ({
                                ...prev,
                                [currentQuestion._id]: {
                                  ...prev[currentQuestion._id],
                                  [selectedLanguage]: value
                                }
                              }));
                            }
                          }}
                          options={{
                            fontSize: 16,
                            minimap: { enabled: true },
                            scrollBeyondLastLine: false,
                            wordWrap: 'on',
                            automaticLayout: true,
                            tabSize: 2,
                            fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                            quickSuggestions: false,
                            suggestOnTriggerCharacters: false,
                            wordBasedSuggestions: false,
                            parameterHints: { enabled: false },
                            acceptSuggestionOnCommitCharacter: false,
                            acceptSuggestionOnEnter: 'off',
                            suggestSelection: 'first',
                            tabCompletion: 'off'
                          }}
                        />
                      </div>
                      
                      <div style={{
                        marginTop: '12px',
                        padding: '10px 12px',
                        background: '#e6ffef',
                        borderRadius: '6px',
                        fontSize: '12px',
                        color: '#22543d',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <span>💡</span>
                        <span>
                          Your code auto-saves locally every 2 seconds. It will be sent to the server when you click "Next" or "Submit".
                        </span>
                      </div>
                    </div>
                  )}

                  {currentQuestion.questionType === 'MCQ' && currentQuestion.options && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {currentQuestion.options.map((option, idx) => (
                        <label key={idx} style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '16px',
                          background: answers[currentQuestion._id] === idx ? '#f0f4ff' : '#f7fafc',
                          border: answers[currentQuestion._id] === idx ? '2px solid #667eea' : '1px solid #e2e8f0',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}>
                          <input
                            type="radio"
                            name={`question-${currentQuestion._id}`}
                            value={idx}
                            checked={answers[currentQuestion._id] === idx}
                            onChange={(e) => handleAnswerChange(currentQuestion._id, e.target.value)}
                            style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                          />
                          <span style={{ color: '#2d3748', fontSize: '15px', flex: 1 }}>{option}</span>
                        </label>
                      ))}
                    </div>
                  )}

                  {currentQuestion.questionType === 'TrueFalse' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '16px',
                        background: answers[currentQuestion._id] === 'true' ? '#f0f4ff' : '#f7fafc',
                        border: answers[currentQuestion._id] === 'true' ? '2px solid #667eea' : '1px solid #e2e8f0',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}>
                        <input
                          type="radio"
                          name={`question-${currentQuestion._id}`}
                          value="true"
                          checked={answers[currentQuestion._id] === 'true'}
                          onChange={(e) => handleAnswerChange(currentQuestion._id, e.target.value)}
                          style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                        />
                        <span style={{ color: '#2d3748', fontSize: '15px', flex: 1 }}>True</span>
                      </label>
                      <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '16px',
                        background: answers[currentQuestion._id] === 'false' ? '#f0f4ff' : '#f7fafc',
                        border: answers[currentQuestion._id] === 'false' ? '2px solid #667eea' : '1px solid #e2e8f0',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}>
                        <input
                          type="radio"
                          name={`question-${currentQuestion._id}`}
                          value="false"
                          checked={answers[currentQuestion._id] === 'false'}
                          onChange={(e) => handleAnswerChange(currentQuestion._id, e.target.value)}
                          style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                        />
                        <span style={{ color: '#2d3748', fontSize: '15px', flex: 1 }}>False</span>
                      </label>
                    </div>
                  )}

                  {/* Coding Question Type */}
                  {(currentQuestion.questionType === 'Coding' || (isAnsweringFollowUp && pendingFollowUps[currentFollowUpIndex]?.requiresCode)) && (
                    <div style={{ marginTop: '16px' }}>
                      {/* Language Selector */}
                      <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <label style={{ fontWeight: 600, fontSize: '14px', color: '#4a5568' }}>
                          Language:
                        </label>
                        <select
                          value={selectedLanguage}
                          onChange={(e) => setSelectedLanguage(e.target.value)}
                          style={{
                            padding: '8px 12px',
                            border: '1px solid #e2e8f0',
                            borderRadius: '6px',
                            fontSize: '14px',
                            backgroundColor: 'white',
                            cursor: 'pointer',
                            outline: 'none'
                          }}
                        >
                          {(currentQuestion.questionType === 'Coding' 
                            ? currentQuestion.codingLanguages || ['javascript', 'python', 'java', 'sql', 'csharp', 'typescript']
                            : pendingFollowUps[currentFollowUpIndex]?.supportedLanguages || ['javascript', 'python', 'java', 'sql', 'csharp', 'typescript']
                          ).map(lang => (
                            <option key={lang} value={lang}>
                              {lang === 'javascript' ? 'JavaScript' :
                               lang === 'python' ? 'Python' :
                               lang === 'java' ? 'Java' :
                               lang === 'sql' ? 'SQL' :
                               lang === 'csharp' ? 'C#' :
                               lang === 'typescript' ? 'TypeScript' : lang}
                            </option>
                          ))}
                        </select>
                        
                        {/* Theme Toggle */}
                        <button
                          onClick={() => setEditorTheme(editorTheme === 'vs-dark' ? 'light' : 'vs-dark')}
                          style={{
                            marginLeft: 'auto',
                            padding: '8px 12px',
                            border: '1px solid #e2e8f0',
                            borderRadius: '6px',
                            background: 'white',
                            cursor: 'pointer',
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          {editorTheme === 'vs-dark' ? '☀️ Light' : '🌙 Dark'}
                        </button>
                      </div>

                      {/* Split Layout: Editor (70%) + Ask Feature (30%) */}
                      <div style={{ 
                        display: 'flex', 
                        gap: '16px',
                        alignItems: 'flex-start'
                      }}>
                        {/* Left Column: Monaco Editor + Expected Output (70%) */}
                        <div style={{ flex: '0 0 70%' }}>
                          {/* Monaco Editor */}
                          <div style={{ 
                            border: '1px solid #e2e8f0', 
                            borderRadius: '8px', 
                            overflow: 'hidden',
                            height: '600px'
                          }}>
                            <Editor
                              height="100%"
                              language={selectedLanguage === 'csharp' ? 'csharp' : selectedLanguage}
                              theme={editorTheme}
                              value={
                                isAnsweringFollowUp 
                                  ? (followUpCodeAnswers[`${currentFollowUpParent}_${currentFollowUpIndex}`] || 
                                     pendingFollowUps[currentFollowUpIndex]?.starterCode?.[selectedLanguage] || 
                                     '// Write your code here...\n')
                                  : (codeAnswers[currentQuestion._id]?.[selectedLanguage] || 
                                     currentQuestion.starterCode?.[selectedLanguage] || 
                                     '// Write your code here...\n')
                              }
                              onChange={(value) => {
                                if (isAnsweringFollowUp) {
                                  const key = `${currentFollowUpParent}_${currentFollowUpIndex}`;
                                  setFollowUpCodeAnswers(prev => ({
                                    ...prev,
                                    [key]: value
                                  }));
                                } else {
                                  setCodeAnswers(prev => ({
                                    ...prev,
                                    [currentQuestion._id]: {
                                      ...prev[currentQuestion._id],
                                      [selectedLanguage]: value
                                    }
                                  }));
                                }
                              }}
                              options={{
                                fontSize: 16,
                                minimap: { enabled: false },
                                scrollBeyondLastLine: false,
                                wordWrap: 'on',
                                automaticLayout: true,
                                tabSize: 2,
                                fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                                quickSuggestions: false,
                                suggestOnTriggerCharacters: false,
                                wordBasedSuggestions: false,
                                parameterHints: { enabled: false },
                                acceptSuggestionOnCommitCharacter: false,
                                acceptSuggestionOnEnter: 'off',
                                suggestSelection: 'first',
                                tabCompletion: 'off'
                              }}
                            />
                          </div>

                          {/* Expected Output (if provided for Coding questions) */}
                          {currentQuestion.expectedOutput && (
                            <div style={{
                              marginTop: '12px',
                              padding: '12px',
                              background: '#f0f9ff',
                              border: '1px solid #bae6fd',
                              borderRadius: '6px'
                            }}>
                              <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#0c4a6e', fontWeight: 600 }}>
                                Expected Output:
                              </h4>
                              <pre style={{ 
                                margin: 0, 
                                fontFamily: 'monospace', 
                                fontSize: '13px', 
                                color: '#1e293b',
                                whiteSpace: 'pre-wrap'
                              }}>
                                {currentQuestion.expectedOutput}
                              </pre>
                            </div>
                          )}
                        </div>

                        {/* Right Column: Ask Interviewer Feature (30%) */}
                        <div style={{ flex: '0 0 calc(30% - 16px)' }}>
                          <div style={{
                            padding: '16px',
                            background: '#fef3c7',
                            border: '2px solid #fbbf24',
                            borderRadius: '8px',
                            height: currentQuestion.expectedOutput ? 'calc(600px + 12px + 100px)' : '600px',
                            overflowY: 'auto',
                            display: 'flex',
                            flexDirection: 'column'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', flexShrink: 0 }}>
                              <h4 style={{ margin: 0, fontSize: '15px', color: '#92400e', fontWeight: 600 }}>
                                💬 Ask the Interviewer
                              </h4>
                              <button
                                onClick={isAsking ? handleStopAsking : handleStartAsking}
                                disabled={isWaitingForAIResponse}
                                style={{
                                  padding: '6px 12px',
                                  background: isAsking ? '#ef4444' : '#10b981',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: isWaitingForAIResponse ? 'not-allowed' : 'pointer',
                                  fontSize: '13px',
                                  fontWeight: 600,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  opacity: isWaitingForAIResponse ? 0.6 : 1,
                                  flexShrink: 0
                                }}
                              >
                                {isWaitingForAIResponse ? (
                                  <>⏳</>
                                ) : isAsking ? (
                                  <>⏹</>
                                ) : (
                                  <>🎤 Ask</>
                                )}
                              </button>
                            </div>
                            
                            {/* Instructions */}
                            {!isAsking && !aiInterviewerResponse && (
                              <p style={{ margin: 0, fontSize: '12px', color: '#78350f', lineHeight: '1.5', flexShrink: 0 }}>
                                Click "Ask" to speak your question about the problem, approach, or clarifications.
                              </p>
                            )}
                            
                            {/* Recording Indicator and Transcript */}
                            {isAsking && (
                              <div style={{
                                padding: '10px',
                                background: 'white',
                                borderRadius: '6px',
                                marginTop: '8px',
                                flexShrink: 0
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                                  <div style={{
                                    width: '8px',
                                    height: '8px',
                                    background: '#ef4444',
                                    borderRadius: '50%',
                                    animation: 'pulse 1.5s infinite'
                                  }} />
                                  <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 600 }}>
                                    Listening...
                                  </span>
                                </div>
                                <div style={{ 
                                  fontSize: '13px', 
                                  color: '#1f2937',
                                  minHeight: '30px',
                                  fontStyle: askTranscript || askInterimTranscript ? 'normal' : 'italic'
                                }}>
                                  {askTranscript || askInterimTranscript || 'Start speaking...'}
                                </div>
                              </div>
                            )}
                            
                            {/* AI Response */}
                            {aiInterviewerResponse && (
                              <div style={{
                                marginTop: '12px',
                                padding: '12px',
                                background: 'white',
                                borderRadius: '6px',
                                border: '1px solid #d97706',
                                flex: '1 1 auto',
                                overflowY: 'auto'
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                                  <span style={{ fontSize: '14px' }}>👨‍💼</span>
                                  <span style={{ fontSize: '12px', fontWeight: 600, color: '#92400e' }}>
                                    Interviewer:
                                  </span>
                                </div>
                                <div style={{ fontSize: '13px', color: '#1f2937', lineHeight: '1.6' }}>
                                  {aiInterviewerResponse}
                                </div>
                                <button
                                  onClick={() => setAiInterviewerResponse(null)}
                                  style={{
                                    marginTop: '8px',
                                    padding: '4px 10px',
                                    background: '#f59e0b',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    fontSize: '11px',
                                    cursor: 'pointer',
                                    fontWeight: 500
                                  }}
                                >
                                  Clear
                                </button>
                              </div>
                            )}
                            
                            {/* Conversation History */}
                            {askConversationHistory.length > 0 && (
                              <details style={{ marginTop: '12px', flexShrink: 0 }}>
                                <summary style={{ 
                                  fontSize: '12px', 
                                  color: '#92400e', 
                                  cursor: 'pointer',
                                  fontWeight: 600,
                                  padding: '6px',
                                  background: 'white',
                                  borderRadius: '4px'
                                }}>
                                  📝 History ({askConversationHistory.length})
                                </summary>
                                <div style={{ 
                                  marginTop: '8px',
                                  maxHeight: '150px',
                                  overflowY: 'auto',
                                  background: 'white',
                                  borderRadius: '4px',
                                  padding: '8px'
                                }}>
                                  {askConversationHistory.map((item, index) => (
                                    <div key={index} style={{ marginBottom: '10px', paddingBottom: '10px', borderBottom: index < askConversationHistory.length - 1 ? '1px solid #e5e7eb' : 'none' }}>
                                      <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '3px' }}>
                                        <strong>You:</strong> {item.question}
                                      </div>
                                      <div style={{ fontSize: '11px', color: '#374151' }}>
                                        <strong>AI:</strong> {item.response}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </details>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Generating Follow-up Indicator */}
                  {isGeneratingFollowUp && (
                    <div style={{ 
                      marginTop: '20px', 
                      padding: '16px', 
                      background: '#faf5ff', 
                      borderRadius: '8px', 
                      border: '2px dashed #9f7aea',
                      textAlign: 'center'
                    }}>
                      <div style={{ 
                        width: '30px', 
                        height: '30px', 
                        border: '3px solid #e9d8fd',
                        borderTop: '3px solid #9f7aea',
                        borderRadius: '50%',
                        margin: '0 auto 12px',
                        animation: 'spin 1s linear infinite'
                      }}></div>
                      <span style={{ color: '#6b46c1', fontSize: '14px', fontWeight: 500 }}>
                        🤖 AI is preparing follow-up questions based on your answer...
                      </span>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>

        {/* Upload Progress Modal */}
        <Modal
          open={isUploading}
          aria-labelledby="upload-modal-title"
          aria-describedby="upload-modal-description"
          disableScrollLock={true}
          BackdropProps={{
            timeout: 0
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '5%',
              left: '50%',
              transform: 'translateX(-50%)',
              width: { xs: '90%', sm: '500px' },
              maxHeight: '90vh',
              bgcolor: 'background.paper',
              borderRadius: '12px',
              boxShadow: 24,
              p: 4,
              outline: 'none'
            }}
          >
              {uploadStatus === 'success' ? (
                // Success State
                <>
                  <div style={{
                    textAlign: 'center',
                    marginBottom: '20px'
                  }}>
                    <div style={{
                      width: '60px',
                      height: '60px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #48bb78, #38a169)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 16px',
                      fontSize: '32px'
                    }}>
                      ✓
                    </div>
                    <h3 id="upload-modal-title" style={{ color: '#2d3748', fontSize: '18px', marginBottom: '8px' }}>
                      Upload Complete!
                    </h3>
                    <p id="upload-modal-description" style={{ color: '#718096', fontSize: '14px', margin: 0 }}>
                      Your video has been uploaded successfully
                    </p>
                  </div>
                </>
              ) : uploadStatus === 'error' ? (
                // Error State
                <>
                  <div style={{
                    textAlign: 'center',
                    marginBottom: '20px'
                  }}>
                    <div style={{
                      width: '60px',
                      height: '60px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #f56565, #e53e3e)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 16px',
                      fontSize: '32px',
                      color: 'white'
                    }}>
                      ✕
                    </div>
                    <h3 style={{ color: '#2d3748', fontSize: '18px', marginBottom: '8px' }}>
                      Upload Failed
                    </h3>
                    <p style={{ color: '#718096', fontSize: '14px', marginBottom: '12px' }}>
                      {uploadError || 'An error occurred while uploading your video'}
                    </p>
                    <button
                      onClick={() => {
                        setIsUploading(false);
                        setUploadStatus(null);
                      }}
                      style={{
                        padding: '10px 24px',
                        background: 'linear-gradient(135deg, #667eea, #764ba2)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      Close
                    </button>
                  </div>
                </>
              ) : (
                // Uploading State
                <>
                  <h3 style={{ color: '#2d3748', fontSize: '18px', marginBottom: '20px', textAlign: 'center' }}>
                    📹 Uploading Video Recording to Dropbox...
                  </h3>
                  <div style={{
                    width: '100%',
                    height: '30px',
                    background: '#e2e8f0',
                    borderRadius: '15px',
                    overflow: 'hidden',
                    marginBottom: '12px'
                  }}>
                    <div style={{
                      width: `${uploadProgress}%`,
                      height: '100%',
                      background: 'linear-gradient(to right, #667eea, #764ba2)',
                      transition: 'width 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '13px',
                      fontWeight: 600
                    }}>
                      {uploadProgress > 10 && `${uploadProgress}%`}
                    </div>
                  </div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <p style={{ 
                      color: '#718096', 
                      fontSize: '14px',
                      margin: 0
                    }}>
                      {uploadProgress}% complete
                    </p>
                    <div style={{
                      fontSize: '12px',
                      color: '#a0aec0',
                      fontStyle: 'italic'
                    }}>
                      Please wait...
                    </div>
                  </div>
                  {/* Animated dots */}
                  <div style={{
                    textAlign: 'center',
                    marginTop: '16px',
                    color: '#667eea',
                    fontSize: '24px',
                    letterSpacing: '4px'
                  }}>
                    <span style={{ animation: 'pulse 1.4s ease-in-out 0s infinite' }}>•</span>
                    <span style={{ animation: 'pulse 1.4s ease-in-out 0.2s infinite' }}>•</span>
                    <span style={{ animation: 'pulse 1.4s ease-in-out 0.4s infinite' }}>•</span>
                  </div>
                </>
              )}
            </Box>
          </Modal>

        {/* Processing overlay - combines saving answer and activating speech recognition */}
        <Modal
          open={isProcessing}
          aria-labelledby="processing-modal-title"
          aria-describedby="processing-modal-description"
          disableScrollLock={true}
          BackdropProps={{
            timeout: 0
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '5%',
              left: '50%',
              transform: 'translateX(-50%)',
              width: { xs: '90%', sm: '400px' },
              maxHeight: '90vh',
              bgcolor: 'background.paper',
              borderRadius: '12px',
              boxShadow: 24,
              p: 4,
              textAlign: 'center',
              outline: 'none'
            }}
          >
              <div style={{ 
                width: '60px', 
                height: '60px', 
                border: '4px solid #e2e8f0',
                borderTop: '4px solid #667eea',
                borderRadius: '50%',
                margin: '0 auto 20px',
                animation: 'spin 1s linear infinite'
              }}></div>
              <h3 id="processing-modal-title" style={{ color: '#2d3748', fontSize: '18px', marginBottom: '12px' }}>
                Processing...
              </h3>
              <p id="processing-modal-description" style={{ color: '#718096', fontSize: '14px', margin: 0, lineHeight: 1.6 }}>
                Saving your answer and<br/>
                activating speech recognition
              </p>
            </Box>
          </Modal>

        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '20px', paddingTop: '20px', borderTop: '2px solid #e2e8f0' }}>
          {isAnsweringFollowUp ? (
            // Follow-up question navigation
            <>
              <div style={{ 
                flex: 1, 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                padding: '8px 12px',
                background: '#faf5ff',
                borderRadius: '8px',
                border: '1px solid #e9d8fd'
              }}>
                <span style={{ fontSize: '13px', color: '#6b46c1', fontWeight: 500 }}>
                  📋 Follow-up {currentFollowUpIndex + 1} of {pendingFollowUps.length}
                  {currentFollowUpIndex === pendingFollowUps.length - 1 && currentQuestionIndex === interview.questions.length - 1 && (
                    <span style={{ marginLeft: '8px', color: '#48bb78', fontWeight: 600 }}>• Last Question!</span>
                  )}
                </span>
              </div>
              <button 
                onClick={handleNextFollowUp} 
                className="btn btn-primary" 
                style={{ 
                  minWidth: '200px', 
                  background: currentFollowUpIndex === pendingFollowUps.length - 1 && currentQuestionIndex === interview.questions.length - 1 
                    ? '#48bb78' 
                    : '#9f7aea', 
                  borderColor: currentFollowUpIndex === pendingFollowUps.length - 1 && currentQuestionIndex === interview.questions.length - 1 
                    ? '#48bb78' 
                    : '#9f7aea',
                  fontWeight: 600
                }}
                disabled={isProcessing}
              >
                {isProcessing ? 'Saving...' : 
                  currentFollowUpIndex < pendingFollowUps.length - 1 
                    ? 'Next Follow-up →' 
                    : currentQuestionIndex < interview.questions.length - 1 
                      ? 'Continue to Next Question →'
                      : '✓ Complete & Submit Interview'
                }
              </button>
            </>
          ) : currentQuestionIndex < interview.questions.length - 1 ? (
            // Not the last question - show Next button
            <button 
              onClick={handleNext} 
              className="btn btn-primary" 
              style={{ minWidth: '140px' }}
              disabled={isProcessing || isGeneratingFollowUp}
            >
              {isProcessing ? 'Saving...' : isGeneratingFollowUp ? 'Generating...' : 'Save & Next →'}
            </button>
          ) : (
            // Last question - show Submit button
            <>
              {!isGeneratingFollowUp && (
                <div style={{ 
                  flex: 1, 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  padding: '8px 12px',
                  background: '#f0fff4',
                  borderRadius: '8px',
                  border: '1px solid #9ae6b4'
                }}>
                  <span style={{ fontSize: '13px', color: '#22543d', fontWeight: 600 }}>
                    ✓ Final Question
                  </span>
                </div>
              )}
              <button 
                onClick={handleSubmit} 
                className="btn btn-primary" 
                style={{ minWidth: '180px', background: '#667eea', borderColor: '#667eea', fontWeight: 600 }}
                disabled={isProcessing || !isRecognitionReady || isGeneratingFollowUp || isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : isProcessing ? 'Saving...' : isGeneratingFollowUp ? 'Generating...' : '✓ Submit Interview'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
    </>
  );
}

export default TakeInterview;
